// ============================================================================
// COMPREHENSIVE EQUIPMENT TAXONOMY
// ISO 14224 (80+ classes) + SFG20 Building Services + Optimal Reference Library
// ============================================================================
// Structure per class:
//   functions[]    — RCM Q1 functions with quantified performance standards
//   failuremodes[] — ISO 14224 failure modes with mechanism, cause, severity
//   scheduledtasks[] — PM tasks with intervals and strategy (CB/FT)
//   consequences[] — Consequence categories with severity/likelihood
//   tags[]         — Searchable tags for AI fuzzy matching
// ============================================================================

const TAXONOMY = {
  // =========================================================================
  // ISO 14224 — ROTATING EQUIPMENT
  // =========================================================================
  'Centrifugal Pump': {
    category: 'Rotating', standard: 'ISO 14224 §A.1',
    tags: ['pump', 'centrifugal', 'booster', 'feed', 'transfer', 'process pump', 'cooling water pump', 'charge pump', 'reflux pump'],
    functions: [
      { description: 'Transfer fluid from source to destination at required flow rate and pressure', type: 'Primary', performanceStandard: 'Minimum rated flow ±5% at rated head' },
      { description: 'Contain process fluid without leakage to atmosphere', type: 'Primary', performanceStandard: 'Zero visible leakage per API 682' },
      { description: 'Provide continuous flow during normal operating conditions', type: 'Primary', performanceStandard: 'Operating range 80-120% of BEP flow' },
      { description: 'Operate within acceptable vibration limits', type: 'Secondary', performanceStandard: 'ISO 10816 Zone A/B, <4.5mm/s RMS' },
      { description: 'Prevent reverse flow on shutdown', type: 'Protective', performanceStandard: 'Check valve closes within 2 seconds' }
    ],
    failuremodes: [
      { description: 'Seal failure — external leakage', mechanism: 'Mechanical seal wear/degradation', cause: 'Dry running, misalignment, contamination, thermal shock', severity: 7, detectionMethod: 'Visual inspection, seal pressure monitoring' },
      { description: 'Bearing failure — excessive vibration', mechanism: 'Rolling element fatigue, lubrication breakdown', cause: 'Insufficient lubrication, misalignment, overload', severity: 8, detectionMethod: 'Vibration analysis, temperature monitoring' },
      { description: 'Impeller erosion — reduced flow/head', mechanism: 'Cavitation, abrasive wear', cause: 'Low NPSH, entrained solids, off-BEP operation', severity: 5, detectionMethod: 'Performance monitoring, flow measurement' },
      { description: 'Coupling failure — loss of drive', mechanism: 'Fatigue fracture, element degradation', cause: 'Misalignment, overload, age', severity: 6, detectionMethod: 'Vibration analysis, visual inspection' },
      { description: 'Casing crack — structural leak', mechanism: 'Fatigue, corrosion, water hammer', cause: 'Pressure cycling, corrosive fluid, rapid valve closure', severity: 9, detectionMethod: 'UT thickness, visual inspection' },
      { description: 'Wear ring degradation — internal recirculation', mechanism: 'Erosion of clearances', cause: 'Abrasive fluid, off-BEP operation, age', severity: 4, detectionMethod: 'Performance test, power monitoring' }
    ],
    scheduledtasks: [
      { description: 'Vibration monitoring — bearing condition assessment', interval: 720, intervalUnit: 'hours', strategy: 'Condition Based', sfg20Ref: 'SFG20 P1' },
      { description: 'Mechanical seal inspection and replacement', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Oil analysis — bearing lubrication condition', interval: 2160, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Performance test — flow rate, head, power', interval: 4380, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Alignment check — laser alignment', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Coupling inspection and element replacement', interval: 17520, intervalUnit: 'hours', strategy: 'Fixed Time' }
    ],
    consequences: [
      { description: 'Loss of cooling/process fluid supply', category: 'Operational', severity: 8, likelihood: 3 },
      { description: 'Environmental release of process fluid', category: 'Environmental', severity: 7, likelihood: 4 },
      { description: 'Fire/explosion risk from flammable fluid leak', category: 'Safety', severity: 9, likelihood: 2 }
    ]
  },

  'Reciprocating Pump': {
    category: 'Rotating', standard: 'ISO 14224 §A.1',
    tags: ['positive displacement', 'plunger', 'diaphragm', 'metering', 'dosing', 'injection pump', 'triplex', 'quintuplex'],
    functions: [
      { description: 'Deliver fluid at high pressure with positive displacement', type: 'Primary', performanceStandard: 'Rated flow ±5% at rated discharge pressure' },
      { description: 'Maintain packing/plunger seal integrity', type: 'Primary', performanceStandard: 'Leakage below environmental limit' },
      { description: 'Provide pulsation-damped flow to downstream', type: 'Secondary', performanceStandard: 'Pressure pulsation <10% of Pd' }
    ],
    failuremodes: [
      { description: 'Valve failure — loss of pumping efficiency', mechanism: 'Seat/plate erosion, spring fatigue', cause: 'Abrasive fluid, cavitation, high cycling', severity: 6, detectionMethod: 'Performance monitoring, valve temperature' },
      { description: 'Packing/plunger seal leak', mechanism: 'Abrasive wear, thermal degradation', cause: 'Misalignment, dry running, contaminated fluid', severity: 7, detectionMethod: 'Visual inspection, drip rate monitoring' },
      { description: 'Crankshaft/crosshead bearing failure', mechanism: 'Fatigue, lubrication breakdown', cause: 'Oil contamination, overload, misalignment', severity: 8, detectionMethod: 'Oil analysis, vibration monitoring' },
      { description: 'Pulsation dampener failure', mechanism: 'Bladder rupture, gas charge loss', cause: 'Age, incorrect pre-charge, fluid hammer', severity: 6, detectionMethod: 'Pressure monitoring, dampener charge check' }
    ],
    scheduledtasks: [
      { description: 'Valve inspection and replacement', interval: 4380, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Packing adjustment and replacement', interval: 2160, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Crankcase oil analysis', interval: 720, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Pulsation dampener charge check', interval: 720, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Crosshead and bearing clearance check', interval: 17520, intervalUnit: 'hours', strategy: 'Fixed Time' }
    ],
    consequences: [
      { description: 'Loss of chemical injection', category: 'Operational', severity: 7, likelihood: 3 },
      { description: 'Chemical spill from packing failure', category: 'Environmental', severity: 8, likelihood: 3 }
    ]
  },

  'Electric Motor': {
    category: 'Rotating', standard: 'ISO 14224 §A.2',
    tags: ['motor', 'induction motor', 'synchronous motor', 'dc motor', 'variable speed drive', 'vsd', 'vfd', 'mcc'],
    functions: [
      { description: 'Convert electrical energy to mechanical rotational energy', type: 'Primary', performanceStandard: 'Rated power output within ±5%' },
      { description: 'Drive connected equipment at required speed', type: 'Primary', performanceStandard: 'Rated RPM ±2%' },
      { description: 'Operate within thermal limits under all load conditions', type: 'Secondary', performanceStandard: 'Winding temperature <Class F (155°C)' }
    ],
    failuremodes: [
      { description: 'Winding insulation failure — ground fault', mechanism: 'Insulation degradation, thermal aging', cause: 'Overheating, moisture ingress, voltage spikes', severity: 8, detectionMethod: 'Insulation resistance test, PD monitoring' },
      { description: 'Bearing failure — noise/vibration', mechanism: 'Rolling element fatigue', cause: 'Misalignment, over-greasing, contamination', severity: 7, detectionMethod: 'Vibration analysis, temperature monitoring' },
      { description: 'Rotor bar cracking — reduced torque', mechanism: 'Thermal fatigue, centrifugal stress', cause: 'Frequent starts, overload, manufacturing defect', severity: 6, detectionMethod: 'Motor current signature analysis (MCSA)' },
      { description: 'Shaft seal failure — contamination ingress', mechanism: 'Seal lip wear, shaft groove', cause: 'Age, misalignment, hostile environment', severity: 4, detectionMethod: 'Visual inspection' }
    ],
    scheduledtasks: [
      { description: 'Motor current signature analysis (MCSA)', interval: 4380, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Insulation resistance test (megger)', interval: 8760, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Thermographic survey — connections, windings', interval: 2160, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Bearing re-greasing per OEM schedule', interval: 4380, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Vibration trending and analysis', interval: 720, intervalUnit: 'hours', strategy: 'Condition Based' }
    ],
    consequences: [
      { description: 'Loss of driven equipment', category: 'Operational', severity: 7, likelihood: 3 },
      { description: 'Motor fire from winding failure', category: 'Safety', severity: 8, likelihood: 2 }
    ]
  },

  'Compressor': {
    category: 'Rotating', standard: 'ISO 14224 §A.3',
    tags: ['compressor', 'centrifugal compressor', 'reciprocating compressor', 'screw compressor', 'axial compressor', 'gas compressor', 'air compressor', 'refrigeration compressor'],
    functions: [
      { description: 'Compress gas from suction to discharge pressure', type: 'Primary', performanceStandard: 'Rated pressure ratio at rated flow' },
      { description: 'Maintain gas seal integrity', type: 'Primary', performanceStandard: 'Zero process gas leakage to atmosphere' },
      { description: 'Operate within surge/stonewall limits', type: 'Secondary', performanceStandard: 'Minimum 10% surge margin' }
    ],
    failuremodes: [
      { description: 'Surge — flow reversal and pulsation', mechanism: 'Aerodynamic instability', cause: 'Low flow, fouled internals, control failure', severity: 9, detectionMethod: 'Anti-surge controller, vibration monitoring' },
      { description: 'Dry gas seal failure', mechanism: 'Seal face wear, contamination', cause: 'Dirty seal gas, coking, loss of buffer pressure', severity: 8, detectionMethod: 'Seal vent monitoring, dP trending' },
      { description: 'Bearing failure — high vibration', mechanism: 'Oil film breakdown, pad damage', cause: 'Oil contamination, misalignment, overload', severity: 8, detectionMethod: 'Vibration and temperature monitoring' },
      { description: 'Fouling — reduced efficiency/capacity', mechanism: 'Deposit build-up on internals', cause: 'Polymerisation, corrosion products, liquid carry-over', severity: 5, detectionMethod: 'Performance monitoring, polytropic efficiency' },
      { description: 'Valve failure (reciprocating) — loss of compression', mechanism: 'Plate/spring fatigue, seat erosion', cause: 'Liquid slugging, high cycling, corrosion', severity: 6, detectionMethod: 'Valve temperature, capacity monitoring' }
    ],
    scheduledtasks: [
      { description: 'Performance monitoring — polytropic efficiency', interval: 168, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Vibration monitoring — proximity probes', interval: 24, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Oil analysis — bearing and seal oil', interval: 720, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Anti-surge valve function test', interval: 4380, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Dry gas seal secondary vent check', interval: 168, intervalUnit: 'hours', strategy: 'Condition Based' }
    ],
    consequences: [
      { description: 'Total loss of gas compression', category: 'Operational', severity: 9, likelihood: 2 },
      { description: 'Gas release from seal failure', category: 'Safety', severity: 9, likelihood: 2 }
    ]
  },

  'Gas Turbine': {
    category: 'Rotating', standard: 'ISO 14224 §A.4',
    tags: ['gas turbine', 'gt', 'aeroderivative', 'frame turbine', 'industrial gas turbine', 'turbine generator', 'turbo generator'],
    functions: [
      { description: 'Convert fuel energy to mechanical shaft power', type: 'Primary', performanceStandard: 'Rated power output at ISO conditions' },
      { description: 'Drive generator/compressor at rated speed', type: 'Primary', performanceStandard: 'Speed ±0.5%' },
      { description: 'Maintain exhaust emissions within permit limits', type: 'Regulatory', performanceStandard: 'NOx, CO, SOx below permit thresholds' }
    ],
    failuremodes: [
      { description: 'Compressor fouling — reduced output/efficiency', mechanism: 'Inlet air contaminant deposition', cause: 'Poor filtration, coastal salt, industrial pollution', severity: 5, detectionMethod: 'Performance monitoring, compressor map' },
      { description: 'Hot gas path degradation — blade/vane distress', mechanism: 'Creep, oxidation, thermal fatigue', cause: 'High firing temperature, fuel contaminants', severity: 9, detectionMethod: 'Borescope inspection, exhaust temperature spread' },
      { description: 'Bearing failure — high vibration', mechanism: 'Oil film instability, babbitt fatigue', cause: 'Oil contamination, misalignment', severity: 8, detectionMethod: 'Proximity probe monitoring' },
      { description: 'Combustion system failure — flashback/flame-out', mechanism: 'Fuel nozzle plugging, liner cracking', cause: 'Fuel quality variation, thermal stress', severity: 8, detectionMethod: 'Combustion dynamics monitoring' },
      { description: 'Control system fault — spurious trip', mechanism: 'Sensor drift, logic error', cause: 'Vibration, corrosion, software bug', severity: 7, detectionMethod: 'Trip analysis, sensor trending' }
    ],
    scheduledtasks: [
      { description: 'Compressor water wash — online/offline', interval: 720, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Borescope inspection — hot gas path', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Vibration trending and analysis', interval: 24, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Lube oil analysis — wear metals, viscosity', interval: 720, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Hot gas path inspection (HGPI)', interval: 25000, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Major overhaul — rotor and casing', interval: 50000, intervalUnit: 'hours', strategy: 'Fixed Time' }
    ],
    consequences: [
      { description: 'Total loss of power generation', category: 'Operational', severity: 10, likelihood: 2 },
      { description: 'Blade liberation causing casing breach', category: 'Safety', severity: 10, likelihood: 1 },
      { description: 'Emissions permit exceedance', category: 'Regulatory', severity: 7, likelihood: 3 }
    ]
  },

  'Steam Turbine': {
    category: 'Rotating', standard: 'ISO 14224 §A.5',
    tags: ['steam turbine', 'condensing turbine', 'back-pressure turbine', 'extraction turbine', 'turbine drive'],
    functions: [
      { description: 'Convert steam energy to mechanical shaft power', type: 'Primary', performanceStandard: 'Rated MW at design steam conditions' },
      { description: 'Maintain rotor-stator clearances', type: 'Primary', performanceStandard: 'No rubbing at operating conditions' }
    ],
    failuremodes: [
      { description: 'Blade erosion — efficiency loss', mechanism: 'Wet steam/solid particle erosion', cause: 'High moisture, boiler carryover', severity: 6, detectionMethod: 'Performance monitoring, vibration' },
      { description: 'Bearing failure — high vibration/temperature', mechanism: 'Oil film breakdown, babbitt fatigue', cause: 'Oil contamination, thermal transients', severity: 8, detectionMethod: 'Proximity probes, RTD monitoring' },
      { description: 'Gland seal leak — steam loss', mechanism: 'Carbon ring wear, shaft scoring', cause: 'Thermal cycling, rotor eccentricity', severity: 4, detectionMethod: 'Visual inspection, gland condenser vacuum' },
      { description: 'Governor/control valve malfunction', mechanism: 'Servo valve sticking, feedback fault', cause: 'Oil contamination, control system fault', severity: 8, detectionMethod: 'Valve position monitoring, trip testing' }
    ],
    scheduledtasks: [
      { description: 'Vibration monitoring — shaft/bearing', interval: 24, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Lube oil analysis and filtration check', interval: 720, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Governor valve stroke test', interval: 4380, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Major inspection — casing open, blade inspection', interval: 50000, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Over-speed trip test', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' }
    ],
    consequences: [
      { description: 'Loss of power/driven equipment', category: 'Operational', severity: 9, likelihood: 2 },
      { description: 'Over-speed disc burst', category: 'Safety', severity: 10, likelihood: 1 }
    ]
  },

  'Fan/Blower': {
    category: 'Rotating', standard: 'ISO 14224 §A.6',
    tags: ['fan', 'blower', 'induced draft', 'forced draft', 'id fan', 'fd fan', 'exhaust fan', 'ventilation fan', 'process fan'],
    functions: [
      { description: 'Provide airflow/gas flow at required volume and pressure', type: 'Primary', performanceStandard: 'Rated flow ±10% at rated static pressure' },
      { description: 'Maintain combustion/ventilation air supply', type: 'Primary', performanceStandard: 'Continuous operation without stall' }
    ],
    failuremodes: [
      { description: 'Bearing failure — vibration/noise', mechanism: 'Rolling element fatigue, lubrication breakdown', cause: 'Misalignment, contamination, imbalance', severity: 7, detectionMethod: 'Vibration analysis, temperature monitoring' },
      { description: 'Impeller/blade erosion or buildup', mechanism: 'Abrasive/corrosive wear, deposit accumulation', cause: 'Particulate in gas stream, corrosive environment', severity: 5, detectionMethod: 'Performance monitoring, vibration (imbalance)' },
      { description: 'Belt/drive failure', mechanism: 'Belt wear, sheave misalignment', cause: 'Age, incorrect tension, environmental degradation', severity: 4, detectionMethod: 'Visual inspection, slip monitoring' },
      { description: 'Damper/vane malfunction', mechanism: 'Linkage failure, actuator fault', cause: 'Corrosion, mechanical wear, control signal loss', severity: 5, detectionMethod: 'Position feedback, performance test' }
    ],
    scheduledtasks: [
      { description: 'Vibration monitoring — bearing and imbalance check', interval: 720, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Belt tension and condition inspection', interval: 2160, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Bearing lubrication and temperature check', interval: 720, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Impeller/blade condition inspection', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Damper linkage and actuator test', interval: 4380, intervalUnit: 'hours', strategy: 'Fixed Time' }
    ],
    consequences: [
      { description: 'Loss of ventilation/combustion air', category: 'Operational', severity: 7, likelihood: 3 },
      { description: 'Gas accumulation in confined space', category: 'Safety', severity: 9, likelihood: 2 }
    ]
  },

  'Gearbox': {
    category: 'Rotating', standard: 'ISO 14224 §A.7',
    tags: ['gearbox', 'gear box', 'speed reducer', 'speed increaser', 'epicyclic', 'planetary gearbox', 'worm gear', 'helical gear'],
    functions: [
      { description: 'Transmit torque and convert speed', type: 'Primary', performanceStandard: 'Rated torque at design speed ratio' },
      { description: 'Maintain gear mesh alignment', type: 'Primary', performanceStandard: 'Contact pattern per OEM spec' }
    ],
    failuremodes: [
      { description: 'Gear tooth failure — pitting/scuffing/fracture', mechanism: 'Surface fatigue, adhesive wear, bending fatigue', cause: 'Overload, misalignment, lubrication breakdown', severity: 8, detectionMethod: 'Oil analysis, vibration (gear mesh)' },
      { description: 'Bearing failure — vibration/temperature', mechanism: 'Rolling element fatigue, cage failure', cause: 'Contaminated oil, misalignment', severity: 7, detectionMethod: 'Vibration analysis, oil analysis' },
      { description: 'Oil seal leak — lubricant loss', mechanism: 'Seal lip wear', cause: 'Age, misalignment, shaft damage', severity: 4, detectionMethod: 'Visual inspection' }
    ],
    scheduledtasks: [
      { description: 'Oil analysis — wear metals, viscosity, moisture', interval: 720, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Vibration analysis — gear mesh frequencies', interval: 720, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Oil change', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Alignment check', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Internal inspection — borescope/cover removal', interval: 26280, intervalUnit: 'hours', strategy: 'Fixed Time' }
    ],
    consequences: [
      { description: 'Catastrophic gearbox failure, driven equipment damage', category: 'Operational', severity: 8, likelihood: 2 }
    ]
  },

  'Generator': {
    category: 'Rotating', standard: 'ISO 14224 §A.8',
    tags: ['generator', 'alternator', 'synchronous generator', 'genset', 'diesel generator', 'emergency generator', 'standby generator'],
    functions: [
      { description: 'Convert mechanical energy to electrical energy', type: 'Primary', performanceStandard: 'Rated MVA at rated V and Hz' },
      { description: 'Maintain voltage and frequency', type: 'Primary', performanceStandard: 'V ±5%, f ±0.5Hz' },
      { description: 'Maintain insulation integrity', type: 'Primary', performanceStandard: 'PD within normal, IR >100MΩ' }
    ],
    failuremodes: [
      { description: 'Stator winding failure — ground/phase fault', mechanism: 'Insulation degradation, partial discharge', cause: 'Overheating, contamination, voltage transients', severity: 10, detectionMethod: 'PD monitoring, insulation resistance' },
      { description: 'Rotor winding fault — field ground', mechanism: 'Insulation wear', cause: 'Thermal cycling, contamination', severity: 8, detectionMethod: 'Field ground detection relay' },
      { description: 'Bearing failure', mechanism: 'Babbitt fatigue, oil film instability', cause: 'Misalignment, oil contamination', severity: 8, detectionMethod: 'Vibration, temperature monitoring' },
      { description: 'Cooling system failure', mechanism: 'H2 leak, cooler tube leak', cause: 'Seal degradation, water chemistry', severity: 7, detectionMethod: 'H2 purity, coolant flow monitoring' }
    ],
    scheduledtasks: [
      { description: 'Partial discharge monitoring', interval: 720, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Insulation resistance and polarisation index', interval: 8760, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'H2 purity and pressure check', interval: 24, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Excitation system inspection', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Stator wedge tap test', interval: 26280, intervalUnit: 'hours', strategy: 'Fixed Time' }
    ],
    consequences: [
      { description: 'Total loss of power', category: 'Operational', severity: 10, likelihood: 2 },
      { description: 'Generator fire', category: 'Safety', severity: 9, likelihood: 1 }
    ]
  },

  // =========================================================================
  // ISO 14224 — HEAT TRANSFER
  // =========================================================================
  'Heat Exchanger': {
    category: 'Heat Transfer', standard: 'ISO 14224 §B.1',
    tags: ['heat exchanger', 'cooler', 'condenser', 'reboiler', 'preheater', 'economiser', 'plate heat exchanger', 'shell and tube', 'fin fan', 'air cooled'],
    functions: [
      { description: 'Transfer heat between process streams at required duty', type: 'Primary', performanceStandard: 'Rated heat duty ±10%' },
      { description: 'Contain both process streams without cross-contamination', type: 'Primary', performanceStandard: 'Zero inter-stream leakage' },
      { description: 'Maintain pressure integrity', type: 'Primary', performanceStandard: 'No leakage at MAWP' }
    ],
    failuremodes: [
      { description: 'Tube fouling — reduced heat transfer', mechanism: 'Scale, biological growth, corrosion deposits', cause: 'Poor water treatment, high temperature', severity: 5, detectionMethod: 'U-value trending, dP monitoring' },
      { description: 'Tube leak — cross-contamination', mechanism: 'Corrosion, erosion, vibration fatigue', cause: 'Corrosive fluid, high velocity, tube vibration', severity: 7, detectionMethod: 'Leak testing, product quality monitoring' },
      { description: 'Gasket/flange leak', mechanism: 'Gasket degradation, bolt relaxation', cause: 'Thermal cycling, incorrect torque', severity: 6, detectionMethod: 'Visual inspection, LDAR' }
    ],
    scheduledtasks: [
      { description: 'Performance monitoring — U-value trending', interval: 720, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Tube bundle cleaning — chemical/mechanical', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Eddy current tube inspection', interval: 26280, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Gasket and bolt torque check', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' }
    ],
    consequences: [
      { description: 'Reduced process cooling/heating capacity', category: 'Operational', severity: 6, likelihood: 4 },
      { description: 'Product cross-contamination', category: 'Operational', severity: 8, likelihood: 2 }
    ]
  },

  'Boiler': {
    category: 'Heat Transfer', standard: 'ISO 14224 §B.2',
    tags: ['boiler', 'steam boiler', 'water tube', 'fire tube', 'hrsg', 'waste heat boiler', 'packaged boiler'],
    functions: [
      { description: 'Generate steam at rated pressure and temperature', type: 'Primary', performanceStandard: 'Rated steam flow at design conditions' },
      { description: 'Maintain combustion efficiency', type: 'Primary', performanceStandard: 'Efficiency >85%, O2 within range' },
      { description: 'Maintain pressure part integrity', type: 'Safety', performanceStandard: 'No tube leaks, drum intact' }
    ],
    failuremodes: [
      { description: 'Tube failure — boiler tube leak', mechanism: 'Corrosion, erosion, fatigue, overheating', cause: 'Water chemistry excursion, flame impingement', severity: 8, detectionMethod: 'Steam/water leak detection, make-up water rate' },
      { description: 'Burner malfunction — flame instability', mechanism: 'Nozzle fouling, ignition failure', cause: 'Fuel quality, atomiser wear', severity: 7, detectionMethod: 'Flame scanner, combustion monitoring' },
      { description: 'Refractory failure', mechanism: 'Spalling, erosion, thermal shock', cause: 'Rapid temperature changes, flame impingement', severity: 5, detectionMethod: 'Casing temperature survey' },
      { description: 'Feedwater system fault — drum level excursion', mechanism: 'Control valve malfunction, transmitter failure', cause: 'Control system fault', severity: 8, detectionMethod: 'Drum level monitoring, alarm' }
    ],
    scheduledtasks: [
      { description: 'Water-side inspection — drum and tubes', interval: 17520, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Tube thickness measurement — UT', interval: 17520, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Burner inspection and nozzle cleaning', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Safety valve lift test', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Combustion optimisation — O2/CO trim', interval: 2160, intervalUnit: 'hours', strategy: 'Condition Based' }
    ],
    consequences: [
      { description: 'Loss of steam supply', category: 'Operational', severity: 9, likelihood: 2 },
      { description: 'Boiler explosion', category: 'Safety', severity: 10, likelihood: 1 }
    ]
  },

  'Furnace/Fired Heater': {
    category: 'Heat Transfer', standard: 'ISO 14224 §B.3',
    tags: ['furnace', 'fired heater', 'process heater', 'reformer', 'cracker furnace', 'thermal oxidiser'],
    functions: [
      { description: 'Heat process fluid to required outlet temperature', type: 'Primary', performanceStandard: 'Coil outlet temperature ±5°C' },
      { description: 'Maintain tube wall integrity under design conditions', type: 'Safety', performanceStandard: 'Tube metal temperature below design limit' },
      { description: 'Maintain draft and combustion efficiency', type: 'Primary', performanceStandard: 'Draft per design, excess O2 2-4%' }
    ],
    failuremodes: [
      { description: 'Tube failure — process leak into firebox', mechanism: 'Creep rupture, corrosion, erosion, coking', cause: 'Overheating, flame impingement, sulphidation', severity: 10, detectionMethod: 'Tube skin temperature monitoring, UT' },
      { description: 'Burner deterioration — uneven heat distribution', mechanism: 'Tip erosion, fuel passage plugging', cause: 'Fuel quality, flame impingement on tips', severity: 5, detectionMethod: 'Flame pattern observation, thermography' },
      { description: 'Refractory failure — hot spots', mechanism: 'Spalling, erosion, anchor failure', cause: 'Thermal cycling, mechanical damage', severity: 6, detectionMethod: 'Casing temperature survey, visual inspection' },
      { description: 'Convection section fouling', mechanism: 'Soot/ash accumulation, acid dew point corrosion', cause: 'Incomplete combustion, low metal temperature', severity: 4, detectionMethod: 'Stack temperature, draught monitoring' }
    ],
    scheduledtasks: [
      { description: 'Tube skin temperature monitoring', interval: 24, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Tube thickness measurement — UT survey', interval: 17520, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Burner tip inspection and cleaning', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Refractory inspection — turnaround', interval: 26280, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Combustion optimisation', interval: 4380, intervalUnit: 'hours', strategy: 'Condition Based' }
    ],
    consequences: [
      { description: 'Tube rupture causing firebox explosion', category: 'Safety', severity: 10, likelihood: 1 },
      { description: 'Loss of process heating', category: 'Operational', severity: 8, likelihood: 2 }
    ]
  },

  // =========================================================================
  // ISO 14224 — STATIC EQUIPMENT
  // =========================================================================
  'Pressure Vessel': {
    category: 'Static', standard: 'ISO 14224 §C.1',
    tags: ['vessel', 'pressure vessel', 'separator', 'drum', 'knockout drum', 'column', 'tower', 'reactor', 'accumulator', 'surge drum', 'flash drum'],
    functions: [
      { description: 'Contain process fluid at design P and T', type: 'Primary', performanceStandard: 'MAWP per nameplate, zero leakage' },
      { description: 'Provide phase separation', type: 'Primary', performanceStandard: 'Outlet quality per spec' },
      { description: 'Withstand cyclic loading without fatigue', type: 'Primary', performanceStandard: 'Design fatigue life not exceeded' }
    ],
    failuremodes: [
      { description: 'Internal corrosion — wall thinning', mechanism: 'General/pitting/MIC corrosion', cause: 'Corrosive service, poor chemical treatment', severity: 8, detectionMethod: 'UT thickness, corrosion coupon' },
      { description: 'Stress corrosion cracking', mechanism: 'SCC crack propagation', cause: 'Tensile stress + corrosive environment', severity: 9, detectionMethod: 'WFMT, TOFD, phased array UT' },
      { description: 'Nozzle/flange leak', mechanism: 'Gasket degradation, bolt relaxation', cause: 'Thermal cycling, vibration', severity: 6, detectionMethod: 'Visual, LDAR' },
      { description: 'Internal lining failure', mechanism: 'Coating disbondment, erosion', cause: 'Thermal shock, mechanical impact', severity: 5, detectionMethod: 'Holiday test, visual inspection' },
      { description: 'Relief device failure', mechanism: 'Seat leakage, set point drift, blocked inlet', cause: 'Corrosion, fouling, incorrect maintenance', severity: 9, detectionMethod: 'Pop test, acoustic monitoring' }
    ],
    scheduledtasks: [
      { description: 'UT thickness measurement', interval: 17520, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Internal visual inspection', interval: 26280, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'External CUI assessment', interval: 17520, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Relief valve test', interval: 26280, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Flange bolt torque verification', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' }
    ],
    consequences: [
      { description: 'Catastrophic vessel rupture', category: 'Safety', severity: 10, likelihood: 1 },
      { description: 'Process fluid release', category: 'Environmental', severity: 8, likelihood: 3 }
    ]
  },

  'Storage Tank': {
    category: 'Static', standard: 'ISO 14224 §C.2',
    tags: ['tank', 'storage tank', 'atmospheric tank', 'floating roof', 'fixed roof', 'bullet', 'sphere', 'lpg sphere', 'crude tank', 'product tank', 'water tank'],
    functions: [
      { description: 'Store product at required volume and conditions', type: 'Primary', performanceStandard: 'Design capacity, temperature within limits' },
      { description: 'Contain product without leakage to environment', type: 'Primary', performanceStandard: 'Zero floor/shell leakage' },
      { description: 'Maintain safe operating envelope', type: 'Safety', performanceStandard: 'Level, pressure, temperature within limits' }
    ],
    failuremodes: [
      { description: 'Floor plate corrosion — underside', mechanism: 'Soil-side corrosion, CP inadequacy', cause: 'Poor CP, water table, soil chemistry', severity: 8, detectionMethod: 'MFL floor scan, UT thickness' },
      { description: 'Shell corrosion — internal', mechanism: 'Product-side corrosion, waterline attack', cause: 'Corrosive product, water bottom, H2S', severity: 7, detectionMethod: 'UT survey, internal inspection' },
      { description: 'Floating roof seal failure', mechanism: 'Seal degradation, rim gap', cause: 'UV exposure, mechanical damage, tank shell irregularity', severity: 5, detectionMethod: 'Visual inspection, emissions monitoring' },
      { description: 'Foundation settlement — shell distortion', mechanism: 'Differential settlement', cause: 'Soil compaction, water table change, overloading', severity: 7, detectionMethod: 'Settlement survey, shell plumbness check' }
    ],
    scheduledtasks: [
      { description: 'External visual inspection — shell, roof, foundation', interval: 4380, intervalUnit: 'hours', strategy: 'Condition Based', sfg20Ref: 'API 653' },
      { description: 'Cathodic protection survey', interval: 8760, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Internal inspection — floor scan, shell UT', interval: 87600, intervalUnit: 'hours', strategy: 'Fixed Time', sfg20Ref: 'API 653' },
      { description: 'Floating roof seal inspection', interval: 8760, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Settlement survey', interval: 26280, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Tank vent/PV valve test', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' }
    ],
    consequences: [
      { description: 'Major product release to bund', category: 'Environmental', severity: 9, likelihood: 2 },
      { description: 'Tank fire — full surface or rim seal', category: 'Safety', severity: 10, likelihood: 1 }
    ]
  },

  'Piping System': {
    category: 'Static', standard: 'ISO 14224 §C.3',
    tags: ['piping', 'pipe', 'pipeline', 'pipework', 'process piping', 'utility piping', 'small bore connection', 'SBC'],
    functions: [
      { description: 'Transport process fluid between equipment', type: 'Primary', performanceStandard: 'Design flow at allowable dP' },
      { description: 'Maintain pressure integrity', type: 'Primary', performanceStandard: 'Zero leakage at MAOP' }
    ],
    failuremodes: [
      { description: 'CUI — corrosion under insulation', mechanism: 'Moisture-driven corrosion', cause: 'Damaged insulation, cyclic temperature', severity: 7, detectionMethod: 'Profile radiography, pulsed eddy current, insulation removal' },
      { description: 'Internal erosion-corrosion', mechanism: 'Flow-accelerated corrosion', cause: 'High velocity, acidic conditions', severity: 7, detectionMethod: 'UT thickness, guided wave UT' },
      { description: 'Vibration fatigue cracking', mechanism: 'Fatigue at welds/branches', cause: 'Flow-induced vibration, poor support', severity: 8, detectionMethod: 'Visual, vibration assessment' },
      { description: 'Flange/joint leak', mechanism: 'Gasket failure, bolt relaxation', cause: 'Thermal cycling, vibration', severity: 5, detectionMethod: 'LDAR, visual' },
      { description: 'Small bore connection failure', mechanism: 'Vibration fatigue, corrosion', cause: 'Unsupported SBCs, cyclic loading', severity: 7, detectionMethod: 'Visual, SBC inspection programme' }
    ],
    scheduledtasks: [
      { description: 'UT thickness survey — critical circuits', interval: 17520, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'CUI inspection — targeted insulation removal', interval: 26280, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'SBC inspection programme', interval: 17520, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Pipe support/hanger inspection', interval: 17520, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Vibration assessment — problem circuits', interval: 8760, intervalUnit: 'hours', strategy: 'Condition Based' }
    ],
    consequences: [
      { description: 'Hydrocarbon release — fire/explosion', category: 'Safety', severity: 9, likelihood: 2 },
      { description: 'Environmental contamination', category: 'Environmental', severity: 7, likelihood: 3 }
    ]
  },

  // =========================================================================
  // ISO 14224 — ELECTRICAL
  // =========================================================================
  'Transformer': {
    category: 'Electrical', standard: 'ISO 14224 §D.1',
    tags: ['transformer', 'power transformer', 'distribution transformer', 'step-up', 'step-down', 'auto-transformer', 'instrument transformer', 'CT', 'VT'],
    functions: [
      { description: 'Transform voltage between primary and secondary', type: 'Primary', performanceStandard: 'Rated MVA at rated ratio' },
      { description: 'Maintain dielectric integrity', type: 'Primary', performanceStandard: 'Oil BDV >30kV, tan delta <0.5%' },
      { description: 'Dissipate losses to maintain winding temperature', type: 'Primary', performanceStandard: 'Hot spot <120°C' }
    ],
    failuremodes: [
      { description: 'Winding insulation failure', mechanism: 'Paper/oil degradation, PD', cause: 'Overloading, moisture, age, through-faults', severity: 10, detectionMethod: 'DGA, FRA, PD monitoring' },
      { description: 'Bushing failure — flashover', mechanism: 'Insulation degradation', cause: 'Age, pollution, manufacturing defect', severity: 9, detectionMethod: 'Power factor test, thermography, DGA' },
      { description: 'OLTC malfunction', mechanism: 'Contact wear, mechanism jam', cause: 'High switching frequency, oil degradation', severity: 7, detectionMethod: 'DGA, dynamic resistance, timing test' },
      { description: 'Oil leak', mechanism: 'Gasket aging, weld crack', cause: 'Thermal cycling, vibration', severity: 5, detectionMethod: 'Visual inspection, oil level monitoring' },
      { description: 'Cooling system failure', mechanism: 'Fan/pump failure, radiator blockage', cause: 'Motor burnout, fouling', severity: 7, detectionMethod: 'Oil temperature, cooling fan status' }
    ],
    scheduledtasks: [
      { description: 'Dissolved gas analysis (DGA)', interval: 2160, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Oil quality test — BDV, moisture, acidity', interval: 8760, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Thermographic survey — connections, bushings', interval: 4380, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Bushing power factor/tan delta test', interval: 26280, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'OLTC oil sample and mechanism inspection', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Winding resistance and turns ratio test', interval: 26280, intervalUnit: 'hours', strategy: 'Fixed Time' }
    ],
    consequences: [
      { description: 'Transformer fire/explosion', category: 'Safety', severity: 10, likelihood: 1 },
      { description: 'Extended power outage', category: 'Operational', severity: 9, likelihood: 2 }
    ]
  },

  'Switchgear': {
    category: 'Electrical', standard: 'ISO 14224 §D.2',
    tags: ['switchgear', 'circuit breaker', 'MV switchgear', 'HV switchgear', 'LV switchboard', 'MCC', 'motor control centre', 'ring main unit', 'RMU', 'contactor', 'ACB', 'VCB', 'SF6 breaker'],
    functions: [
      { description: 'Switch electrical circuits under normal and fault conditions', type: 'Primary', performanceStandard: 'Rated breaking capacity, operating time per spec' },
      { description: 'Provide electrical isolation for maintenance', type: 'Safety', performanceStandard: 'Visible isolation gap, earth switch functional' },
      { description: 'Protect downstream equipment from overcurrent/fault', type: 'Protective', performanceStandard: 'Trip per protection relay settings' }
    ],
    failuremodes: [
      { description: 'Breaker failure to trip — protection failure', mechanism: 'Mechanism binding, trip coil failure', cause: 'Lack of exercising, corrosion, age', severity: 9, detectionMethod: 'Trip testing, timing test' },
      { description: 'Insulation failure — flashover/tracking', mechanism: 'Insulation degradation, pollution', cause: 'Moisture, contamination, partial discharge', severity: 8, detectionMethod: 'PD monitoring, insulation resistance, thermography' },
      { description: 'Contact degradation — overheating', mechanism: 'Contact erosion, oxidation', cause: 'Arcing, high fault current, age', severity: 6, detectionMethod: 'Contact resistance measurement, thermography' },
      { description: 'SF6 gas leak (GIS)', mechanism: 'Seal degradation', cause: 'Age, vibration, temperature cycling', severity: 5, detectionMethod: 'Gas density monitoring, leak detection' }
    ],
    scheduledtasks: [
      { description: 'Circuit breaker trip test and timing', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Thermographic survey — all connections', interval: 4380, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Contact resistance measurement', interval: 17520, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Insulation resistance test', interval: 17520, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Protection relay secondary injection test', interval: 17520, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'SF6 gas quality and leak check (GIS)', interval: 8760, intervalUnit: 'hours', strategy: 'Condition Based' }
    ],
    consequences: [
      { description: 'Arc flash incident — personnel injury', category: 'Safety', severity: 10, likelihood: 1 },
      { description: 'Uncleared fault — equipment damage cascade', category: 'Operational', severity: 9, likelihood: 2 }
    ]
  },

  'UPS/Battery System': {
    category: 'Electrical', standard: 'ISO 14224 §D.3',
    tags: ['ups', 'uninterruptible power supply', 'battery', 'battery system', 'dc system', 'charger', 'inverter', 'VRLA', 'lead acid', 'lithium ion battery'],
    functions: [
      { description: 'Provide uninterrupted power to critical loads', type: 'Primary', performanceStandard: 'Rated kVA, autonomy time at full load' },
      { description: 'Maintain battery charge state for emergency backup', type: 'Primary', performanceStandard: 'Float voltage per manufacturer, specific gravity >1.215' }
    ],
    failuremodes: [
      { description: 'Battery capacity degradation — insufficient autonomy', mechanism: 'Plate sulphation, grid corrosion, dry-out', cause: 'Age, float voltage, temperature, over-discharge', severity: 7, detectionMethod: 'Capacity test, impedance/conductance' },
      { description: 'Single cell failure — open circuit', mechanism: 'Internal short, connector corrosion', cause: 'Manufacturing defect, thermal runaway, age', severity: 6, detectionMethod: 'Float voltage monitoring, impedance' },
      { description: 'Inverter/charger failure', mechanism: 'Component failure, fan failure', cause: 'Age, thermal stress, power surge', severity: 7, detectionMethod: 'UPS self-test, output monitoring' },
      { description: 'Transfer switch failure — no bypass', mechanism: 'Contact welding, mechanism jam', cause: 'Infrequent operation, corrosion', severity: 5, detectionMethod: 'Transfer test' }
    ],
    scheduledtasks: [
      { description: 'Battery impedance/conductance test', interval: 2160, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Float voltage and specific gravity check', interval: 720, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'UPS load test — transfer to battery', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Battery capacity discharge test', interval: 26280, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Connection torque and thermographic check', interval: 8760, intervalUnit: 'hours', strategy: 'Condition Based' }
    ],
    consequences: [
      { description: 'Loss of critical control/safety systems', category: 'Safety', severity: 9, likelihood: 2 },
      { description: 'Battery thermal runaway — fire/gas release', category: 'Safety', severity: 8, likelihood: 1 }
    ]
  },

  'Variable Speed Drive': {
    category: 'Electrical', standard: 'ISO 14224 §D.4',
    tags: ['vsd', 'vfd', 'variable frequency drive', 'inverter drive', 'soft starter', 'frequency converter', 'ac drive'],
    functions: [
      { description: 'Control motor speed and torque', type: 'Primary', performanceStandard: 'Speed ±0.5% of setpoint, torque regulation ±2%' },
      { description: 'Provide soft-start capability', type: 'Secondary', performanceStandard: 'Current limited to <200% during start' }
    ],
    failuremodes: [
      { description: 'Power module failure — IGBT fault', mechanism: 'Semiconductor failure', cause: 'Overcurrent, overtemperature, voltage spike', severity: 7, detectionMethod: 'Drive fault log, output monitoring' },
      { description: 'Cooling fan failure — overheating', mechanism: 'Fan motor burnout, bearing seizure', cause: 'Dust accumulation, age', severity: 5, detectionMethod: 'Temperature alarm, visual inspection' },
      { description: 'DC bus capacitor degradation', mechanism: 'Electrolyte dry-out, ESR increase', cause: 'Temperature, age, ripple current', severity: 6, detectionMethod: 'Capacitor ESR test, visual inspection' },
      { description: 'Control board failure — loss of speed reference', mechanism: 'Component failure, software fault', cause: 'EMI, power transient, firmware bug', severity: 6, detectionMethod: 'Drive status monitoring' }
    ],
    scheduledtasks: [
      { description: 'Cooling fan inspection and replacement', interval: 17520, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'DC bus capacitor ESR check', interval: 26280, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Thermographic survey — connections/power modules', interval: 4380, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Drive cabinet filter cleaning', interval: 2160, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Firmware backup and version audit', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' }
    ],
    consequences: [
      { description: 'Loss of motor speed control', category: 'Operational', severity: 6, likelihood: 3 },
      { description: 'Harmonic distortion affecting power quality', category: 'Operational', severity: 4, likelihood: 4 }
    ]
  },

  // =========================================================================
  // ISO 14224 — CONTROL & SAFETY
  // =========================================================================
  'Control Valve': {
    category: 'Control & Safety', standard: 'ISO 14224 §E.1',
    tags: ['control valve', 'globe valve', 'butterfly valve', 'ball valve', 'regulating valve', 'throttling valve', 'actuated valve', 'positioner'],
    functions: [
      { description: 'Regulate process flow/pressure to setpoint', type: 'Primary', performanceStandard: 'Control within ±2% of setpoint' },
      { description: 'Fail to safe position on loss of signal/air', type: 'Safety', performanceStandard: 'Fail position reached within 5s' },
      { description: 'Contain fluid without fugitive emissions', type: 'Primary', performanceStandard: 'Zero emissions per EPA Method 21' }
    ],
    failuremodes: [
      { description: 'Valve sticking — erratic control', mechanism: 'Packing friction, corrosion, deposits', cause: 'Infrequent stroking, corrosive fluid', severity: 6, detectionMethod: 'Valve signature test' },
      { description: 'Seat leakage — pass-through', mechanism: 'Seat erosion, cavitation damage', cause: 'High dP, flashing, solids', severity: 5, detectionMethod: 'Leakage test, process monitoring' },
      { description: 'Actuator failure — loss of control', mechanism: 'Diaphragm rupture, spring failure', cause: 'Age, corrosive atmosphere', severity: 7, detectionMethod: 'Valve signature, position feedback' },
      { description: 'Positioner failure — position error', mechanism: 'Electronics failure, feedback linkage', cause: 'Vibration, corrosion, age', severity: 5, detectionMethod: 'Position deviation alarm, calibration check' }
    ],
    scheduledtasks: [
      { description: 'Partial stroke test — SIF valves', interval: 2160, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Valve signature test — friction/travel', interval: 8760, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Positioner calibration check', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Packing leak check and adjustment', interval: 4380, intervalUnit: 'hours', strategy: 'Condition Based' }
    ],
    consequences: [
      { description: 'Loss of process control', category: 'Operational', severity: 6, likelihood: 3 },
      { description: 'Fugitive emissions exceedance', category: 'Regulatory', severity: 5, likelihood: 4 }
    ]
  },

  'Emergency Shutdown Valve': {
    category: 'Control & Safety', standard: 'ISO 14224 §E.2',
    tags: ['esdv', 'sdv', 'shutdown valve', 'blowdown valve', 'depressurisation valve', 'bsdv', 'ssiv', 'hipps'],
    functions: [
      { description: 'Isolate process on ESD demand', type: 'Safety', performanceStandard: 'Full closure within stroke time, SIL-rated PFD' },
      { description: 'Maintain tight shutoff at design pressure', type: 'Safety', performanceStandard: 'Leakage per API 598/ISO 5208' }
    ],
    failuremodes: [
      { description: 'Fails to close on demand', mechanism: 'Seat corrosion, actuator failure, stem binding', cause: 'Deposits, corrosion, lack of stroking', severity: 10, detectionMethod: 'Proof test, partial stroke test' },
      { description: 'Spurious closure', mechanism: 'Solenoid failure, control fault', cause: 'EMI, wiring fault, coil failure', severity: 5, detectionMethod: 'Trip analysis' },
      { description: 'Seat leakage when closed', mechanism: 'Seat erosion, debris', cause: 'Process conditions, infrequent exercising', severity: 7, detectionMethod: 'Seat leakage test' }
    ],
    scheduledtasks: [
      { description: 'Partial stroke test', interval: 2160, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Full stroke test — closure time verification', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Seat leakage test', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Solenoid valve function test', interval: 4380, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Actuator inspection', interval: 26280, intervalUnit: 'hours', strategy: 'Fixed Time' }
    ],
    consequences: [
      { description: 'Safety function fails — hazardous event not mitigated', category: 'Safety', severity: 10, likelihood: 1 }
    ]
  },

  'Safety Instrumented System': {
    category: 'Control & Safety', standard: 'IEC 61511',
    tags: ['sis', 'sif', 'safety system', 'esd system', 'fire and gas', 'f&g', 'emergency shutdown', 'hipps', 'safety plc', 'safety controller'],
    functions: [
      { description: 'Detect hazardous condition and initiate protective action', type: 'Safety', performanceStandard: 'SIL-rated PFD per IEC 61511' },
      { description: 'Execute shutdown logic within process safety time', type: 'Safety', performanceStandard: 'Response time < process safety time' },
      { description: 'Maintain independence from BPCS', type: 'Regulatory', performanceStandard: 'Separation per IEC 61511 Clause 11' }
    ],
    failuremodes: [
      { description: 'Sensor dangerous undetected failure', mechanism: 'Drift, stuck, common-cause failure', cause: 'Fouling, corrosion, age', severity: 10, detectionMethod: 'Proof test, diagnostics' },
      { description: 'Logic solver spurious trip', mechanism: 'I/O fault, software error', cause: 'Power transient, component failure', severity: 5, detectionMethod: 'Trip analysis, diagnostics' },
      { description: 'Final element fails to operate', mechanism: 'Valve stuck, actuator failure', cause: 'Corrosion, friction, air supply loss', severity: 10, detectionMethod: 'Proof test, partial stroke test' },
      { description: 'Common cause failure', mechanism: 'Shared stress', cause: 'Same manufacturer, shared utility', severity: 10, detectionMethod: 'CCF defence review' }
    ],
    scheduledtasks: [
      { description: 'Proof test — full functional test per SIL', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Partial stroke test — final elements', interval: 2160, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Transmitter calibration verification', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'SIS logic validation and software audit', interval: 26280, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'CCF defence review', interval: 26280, intervalUnit: 'hours', strategy: 'Fixed Time' }
    ],
    consequences: [
      { description: 'Loss of safety function — major accident potential', category: 'Safety', severity: 10, likelihood: 1 }
    ]
  },

  'Programmable Logic Controller': {
    category: 'Control & Safety', standard: 'ISO 14224 §E.3',
    tags: ['plc', 'controller', 'dcs', 'distributed control', 'scada', 'rtu', 'control system', 'automation'],
    functions: [
      { description: 'Execute process control logic', type: 'Primary', performanceStandard: 'Scan time <100ms, 99.99% uptime' },
      { description: 'Process field signals and generate outputs', type: 'Primary', performanceStandard: 'All I/O channels functional' },
      { description: 'Provide data to operator interface', type: 'Secondary', performanceStandard: 'Latency <500ms' }
    ],
    failuremodes: [
      { description: 'CPU module failure', mechanism: 'Component failure, firmware corruption', cause: 'Power surge, age, environmental stress', severity: 9, detectionMethod: 'Diagnostics, watchdog' },
      { description: 'I/O card failure', mechanism: 'Component failure', cause: 'Voltage spike, moisture, vibration', severity: 7, detectionMethod: 'Diagnostics, channel monitoring' },
      { description: 'Communication failure', mechanism: 'Network interface failure', cause: 'EMI, connector failure, switch fault', severity: 6, detectionMethod: 'Heartbeat monitoring' },
      { description: 'Power supply failure', mechanism: 'Capacitor degradation, fan failure', cause: 'Age, dust, input voltage variation', severity: 8, detectionMethod: 'Output voltage monitoring' }
    ],
    scheduledtasks: [
      { description: 'Battery backup test and replacement', interval: 17520, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'I/O loop testing — sample verification', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Firmware backup and version audit', interval: 4380, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Environmental check — temperature, dust', interval: 2160, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Network switch and comms health check', interval: 4380, intervalUnit: 'hours', strategy: 'Condition Based' }
    ],
    consequences: [
      { description: 'Total loss of automatic control', category: 'Operational', severity: 9, likelihood: 2 }
    ]
  },

  'Instrument Transmitter': {
    category: 'Control & Safety', standard: 'ISO 14224 §E.4',
    tags: ['transmitter', 'pressure transmitter', 'temperature transmitter', 'flow transmitter', 'level transmitter', 'analyser', 'gas detector', 'flame detector', 'instrument'],
    functions: [
      { description: 'Measure process variable and transmit signal', type: 'Primary', performanceStandard: 'Accuracy ±0.1% of span' },
      { description: 'Provide continuous measurement to control system', type: 'Primary', performanceStandard: 'Update rate per loop requirements' }
    ],
    failuremodes: [
      { description: 'Sensor drift — inaccurate reading', mechanism: 'Diaphragm creep, electronics drift', cause: 'Temperature cycling, fouling, age', severity: 5, detectionMethod: 'Calibration check, redundant comparison' },
      { description: 'Sensor blockage — frozen reading', mechanism: 'Impulse line plugging', cause: 'Process deposits, freezing', severity: 7, detectionMethod: 'Stale value detection, comparison' },
      { description: 'Electronics failure — signal loss', mechanism: 'Component failure, moisture', cause: 'Power surge, corrosion', severity: 6, detectionMethod: 'Signal monitoring, diagnostics' }
    ],
    scheduledtasks: [
      { description: 'Calibration verification — as-found/as-left', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Impulse line blow-down/flushing', interval: 720, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Transmitter diagnostics check', interval: 4380, intervalUnit: 'hours', strategy: 'Condition Based' }
    ],
    consequences: [
      { description: 'Incorrect process control', category: 'Operational', severity: 5, likelihood: 4 },
      { description: 'Loss of safety-critical measurement', category: 'Safety', severity: 8, likelihood: 2 }
    ]
  },

  // =========================================================================
  // ISO 14224 — MECHANICAL / MATERIAL HANDLING
  // =========================================================================
  'Crane': {
    category: 'Mechanical', standard: 'ISO 14224 §F.1',
    tags: ['crane', 'overhead crane', 'gantry crane', 'mobile crane', 'hoist', 'davit', 'pedestal crane', 'jib crane', 'tower crane'],
    functions: [
      { description: 'Lift and position loads within rated capacity', type: 'Primary', performanceStandard: 'SWL per load chart' },
      { description: 'Provide safe load suspension and travel', type: 'Safety', performanceStandard: 'All safety devices functional' }
    ],
    failuremodes: [
      { description: 'Wire rope degradation', mechanism: 'Fatigue, abrasion, corrosion', cause: 'Overloading, poor lubrication, age', severity: 8, detectionMethod: 'Visual, MRT' },
      { description: 'Brake failure', mechanism: 'Lining wear, spring fatigue', cause: 'Excessive duty, contamination', severity: 9, detectionMethod: 'Brake function test' },
      { description: 'Structural crack', mechanism: 'Fatigue at stress concentration', cause: 'Cyclic loading, overload', severity: 9, detectionMethod: 'NDT, structural inspection' },
      { description: 'Limit switch/overload failure', mechanism: 'Contact wear, wiring fault', cause: 'Environment, vibration', severity: 7, detectionMethod: 'Function test' }
    ],
    scheduledtasks: [
      { description: 'Wire rope inspection — visual and MRT', interval: 720, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Brake function and adjustment test', interval: 2160, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Limit switch and safety device test', interval: 720, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Structural inspection — welds/connections', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Statutory annual load test', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' }
    ],
    consequences: [
      { description: 'Dropped load — personnel injury/fatality', category: 'Safety', severity: 10, likelihood: 1 }
    ]
  },

  'Conveyor': {
    category: 'Mechanical', standard: 'ISO 14224 §F.2',
    tags: ['conveyor', 'belt conveyor', 'screw conveyor', 'chain conveyor', 'bucket elevator', 'apron feeder', 'vibrating conveyor'],
    functions: [
      { description: 'Transport material at rated throughput', type: 'Primary', performanceStandard: 'Rated t/h ±10%' },
      { description: 'Maintain belt alignment', type: 'Primary', performanceStandard: 'Belt centred, no edge wear' }
    ],
    failuremodes: [
      { description: 'Belt mistracking — spillage', mechanism: 'Asymmetric loading', cause: 'Off-centre feed, idler misalignment', severity: 5, detectionMethod: 'Visual, belt drift switches' },
      { description: 'Belt splice failure', mechanism: 'Splice degradation', cause: 'Fatigue, moisture, overload', severity: 7, detectionMethod: 'Visual, splice monitoring' },
      { description: 'Idler bearing seizure', mechanism: 'Bearing seizure', cause: 'Contamination, sealing failure', severity: 5, detectionMethod: 'Spin test, thermography' },
      { description: 'Drive unit failure', mechanism: 'Motor burnout, gearbox failure', cause: 'Overload, blockage, lubrication failure', severity: 7, detectionMethod: 'Motor current, oil analysis' }
    ],
    scheduledtasks: [
      { description: 'Belt condition inspection', interval: 720, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Idler spin test and replacement', interval: 2160, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Gearbox oil analysis', interval: 2160, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Belt alignment check', interval: 720, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Pull-cord and safety switch test', interval: 168, intervalUnit: 'hours', strategy: 'Fixed Time' }
    ],
    consequences: [
      { description: 'Production stoppage', category: 'Operational', severity: 6, likelihood: 3 },
      { description: 'Belt fire from seized idler', category: 'Safety', severity: 8, likelihood: 2 }
    ]
  },

  // =========================================================================
  // SFG20 — BUILDING SERVICES
  // =========================================================================
  'HVAC System': {
    category: 'Building Services', standard: 'SFG20',
    tags: ['hvac', 'air conditioning', 'ahu', 'air handling unit', 'chiller', 'package unit', 'split system', 'fan coil', 'VRF', 'VRV', 'heat pump', 'comfort cooling'],
    functions: [
      { description: 'Maintain space temperature within range', type: 'Primary', performanceStandard: 'Temperature ±1°C of setpoint' },
      { description: 'Provide adequate ventilation and air quality', type: 'Primary', performanceStandard: 'Outdoor air per ASHRAE 62.1/CIBSE Guide A' },
      { description: 'Control humidity', type: 'Secondary', performanceStandard: 'RH 40-60%' }
    ],
    failuremodes: [
      { description: 'Compressor failure — loss of cooling', mechanism: 'Motor burnout, valve failure, leak', cause: 'Low refrigerant, electrical fault', severity: 7, detectionMethod: 'Refrigerant pressures, motor current' },
      { description: 'Fan failure — loss of airflow', mechanism: 'Motor failure, belt breakage, seizure', cause: 'Belt wear, lubrication failure', severity: 6, detectionMethod: 'Airflow monitoring, motor current' },
      { description: 'Coil fouling — reduced capacity', mechanism: 'Dust accumulation', cause: 'Poor filtration', severity: 4, detectionMethod: 'dP monitoring, performance trend' },
      { description: 'Refrigerant leak — capacity loss', mechanism: 'Joint leak, coil corrosion', cause: 'Vibration, corrosion', severity: 6, detectionMethod: 'Leak detection, pressure trending' },
      { description: 'BMS/controls failure — loss of regulation', mechanism: 'Sensor failure, controller fault', cause: 'Age, calibration drift', severity: 5, detectionMethod: 'Space condition monitoring' }
    ],
    scheduledtasks: [
      { description: 'Filter inspection and replacement', interval: 2160, intervalUnit: 'hours', strategy: 'Fixed Time', sfg20Ref: 'SFG20 15-010' },
      { description: 'Coil cleaning — condenser and evaporator', interval: 4380, intervalUnit: 'hours', strategy: 'Fixed Time', sfg20Ref: 'SFG20 15-020' },
      { description: 'Refrigerant charge check and leak test', interval: 8760, intervalUnit: 'hours', strategy: 'Condition Based', sfg20Ref: 'SFG20 15-030' },
      { description: 'Belt tension and condition check', interval: 2160, intervalUnit: 'hours', strategy: 'Fixed Time', sfg20Ref: 'SFG20 15-040' },
      { description: 'Controls calibration — sensors and actuators', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time', sfg20Ref: 'SFG20 15-050' },
      { description: 'Damper operation and linkage check', interval: 4380, intervalUnit: 'hours', strategy: 'Fixed Time', sfg20Ref: 'SFG20 15-060' },
      { description: 'Drain pan cleaning and condensate check', interval: 2160, intervalUnit: 'hours', strategy: 'Fixed Time', sfg20Ref: 'SFG20 15-070' }
    ],
    consequences: [
      { description: 'Loss of comfort conditions', category: 'Operational', severity: 5, likelihood: 4 },
      { description: 'Legionella risk from stagnant water', category: 'Safety', severity: 8, likelihood: 2 }
    ]
  },

  'Lift/Elevator': {
    category: 'Building Services', standard: 'SFG20',
    tags: ['lift', 'elevator', 'passenger lift', 'goods lift', 'service lift', 'escalator', 'travelator', 'platform lift'],
    functions: [
      { description: 'Transport passengers/goods between floors safely', type: 'Primary', performanceStandard: 'Rated capacity, levelling accuracy ±5mm' },
      { description: 'Respond to calls within acceptable wait time', type: 'Primary', performanceStandard: 'Average wait <60s peak, <30s off-peak' },
      { description: 'Maintain all safety interlocks and emergency systems', type: 'Safety', performanceStandard: 'All safety devices functional per EN 81' }
    ],
    failuremodes: [
      { description: 'Door operator failure — doors not opening/closing', mechanism: 'Motor failure, controller fault, mechanical jam', cause: 'Wear, debris in track, belt failure', severity: 6, detectionMethod: 'Door timing monitoring, fault logs' },
      { description: 'Drive/controller failure — lift out of service', mechanism: 'Drive fault, encoder failure, software error', cause: 'Component age, power disturbance, EMI', severity: 7, detectionMethod: 'Controller diagnostics, fault logs' },
      { description: 'Rope/belt wear — degraded safety margin', mechanism: 'Wire fatigue, sheave wear', cause: 'Cyclic loading, misalignment, insufficient lubrication', severity: 8, detectionMethod: 'Rope inspection, MRT' },
      { description: 'Safety gear/buffer malfunction', mechanism: 'Mechanical seizure, governor fault', cause: 'Corrosion, lack of testing, age', severity: 9, detectionMethod: 'Statutory safety gear test' }
    ],
    scheduledtasks: [
      { description: 'Monthly safety check — doors, interlocks, emergency phone', interval: 720, intervalUnit: 'hours', strategy: 'Fixed Time', sfg20Ref: 'SFG20 20-010' },
      { description: 'Quarterly lubrication — guide rails, door tracks, machine', interval: 2160, intervalUnit: 'hours', strategy: 'Fixed Time', sfg20Ref: 'SFG20 20-020' },
      { description: 'Six-monthly comprehensive service', interval: 4380, intervalUnit: 'hours', strategy: 'Fixed Time', sfg20Ref: 'SFG20 20-030' },
      { description: 'Annual rope/belt inspection', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time', sfg20Ref: 'SFG20 20-040' },
      { description: 'Statutory thorough examination (LOLER)', interval: 4380, intervalUnit: 'hours', strategy: 'Fixed Time', sfg20Ref: 'LOLER Reg 9' },
      { description: 'Five-yearly safety gear test', interval: 43800, intervalUnit: 'hours', strategy: 'Fixed Time', sfg20Ref: 'EN 81-1/2 Annex F' }
    ],
    consequences: [
      { description: 'Passenger entrapment', category: 'Safety', severity: 7, likelihood: 3 },
      { description: 'Lift out of service — building access impaired', category: 'Operational', severity: 5, likelihood: 4 }
    ]
  },

  'Fire Detection/Suppression': {
    category: 'Building Services', standard: 'SFG20',
    tags: ['fire alarm', 'fire detection', 'sprinkler', 'fire suppression', 'deluge', 'foam system', 'gas suppression', 'fire hydrant', 'dry riser', 'wet riser', 'smoke detector', 'heat detector'],
    functions: [
      { description: 'Detect fire/smoke and raise alarm', type: 'Safety', performanceStandard: 'Detection within 60s of fire event, alarm within 5s of detection' },
      { description: 'Suppress/extinguish fire', type: 'Safety', performanceStandard: 'Activation within design time, water density per design' },
      { description: 'Alert occupants and enable evacuation', type: 'Safety', performanceStandard: 'Audible alarm >65dB throughout, visual alert in accessible areas' }
    ],
    failuremodes: [
      { description: 'Detector failure — undetected fire', mechanism: 'Sensor degradation, contamination', cause: 'Dust, age, environmental exposure', severity: 10, detectionMethod: 'Detector functional test, drift compensation' },
      { description: 'Sprinkler head failure — no water release', mechanism: 'Painted-over head, glass bulb degradation', cause: 'Maintenance damage, corrosion, age', severity: 9, detectionMethod: 'Visual inspection, sample testing' },
      { description: 'Fire pump failure — no water supply', mechanism: 'Pump/motor failure, controller fault', cause: 'Lack of testing, battery failure, pipe corrosion', severity: 9, detectionMethod: 'Weekly pump run test' },
      { description: 'False/unwanted alarm — nuisance activation', mechanism: 'Detector sensitivity, environmental triggers', cause: 'Dust, steam, cooking, insect ingress', severity: 3, detectionMethod: 'Alarm log analysis' }
    ],
    scheduledtasks: [
      { description: 'Weekly fire alarm sounder test', interval: 168, intervalUnit: 'hours', strategy: 'Fixed Time', sfg20Ref: 'SFG20 30-010' },
      { description: 'Weekly fire pump run test', interval: 168, intervalUnit: 'hours', strategy: 'Fixed Time', sfg20Ref: 'SFG20 30-020' },
      { description: 'Quarterly detector functional test (25% rotation)', interval: 2160, intervalUnit: 'hours', strategy: 'Fixed Time', sfg20Ref: 'SFG20 30-030 / BS 5839-1' },
      { description: 'Six-monthly sprinkler system inspection', interval: 4380, intervalUnit: 'hours', strategy: 'Fixed Time', sfg20Ref: 'SFG20 30-040 / BS EN 12845' },
      { description: 'Annual fire alarm full system test', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time', sfg20Ref: 'SFG20 30-050' },
      { description: 'Annual fire pump full-flow test', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time', sfg20Ref: 'SFG20 30-060' },
      { description: 'Dry riser inlet/outlet check', interval: 4380, intervalUnit: 'hours', strategy: 'Fixed Time', sfg20Ref: 'SFG20 30-070 / BS 9990' }
    ],
    consequences: [
      { description: 'Undetected fire — loss of life', category: 'Safety', severity: 10, likelihood: 1 },
      { description: 'Fire spread — building loss', category: 'Operational', severity: 10, likelihood: 1 }
    ]
  },

  'Filtration Unit': {
    category: 'Static', standard: 'ISO 14224 §C.4',
    tags: ['filter', 'filtration unit', 'strainer', 'duplex filter', 'cartridge filter', 'bag filter', 'sand filter', 'membrane filter'],
    functions: [
      { description: 'Remove particulate from process stream', type: 'Primary', performanceStandard: 'Outlet clarity per spec, dP < max' },
      { description: 'Contain fluid without leakage', type: 'Primary', performanceStandard: 'Zero external leakage' }
    ],
    failuremodes: [
      { description: 'Element blinding — high dP', mechanism: 'Particle accumulation', cause: 'High loading, media age', severity: 5, detectionMethod: 'dP monitoring' },
      { description: 'Media breakthrough', mechanism: 'Media rupture, seal bypass', cause: 'High dP, thermal shock', severity: 7, detectionMethod: 'Downstream quality monitoring' },
      { description: 'Housing/seal leak', mechanism: 'Gasket degradation', cause: 'Pressure cycling, chemical attack', severity: 6, detectionMethod: 'Visual inspection' }
    ],
    scheduledtasks: [
      { description: 'Differential pressure monitoring', interval: 24, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Filter element replacement', interval: 4380, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Housing inspection and seal replacement', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' }
    ],
    consequences: [
      { description: 'Downstream equipment damage', category: 'Operational', severity: 6, likelihood: 3 }
    ]
  },

  // =========================================================================

  // =========================================================================
  // ISO 14224 — ROTATING EQUIPMENT (Additional)
  // =========================================================================
  'Centrifugal Compressor': {
    category: 'Rotating', standard: 'ISO 14224 §A.4',
    tags: ['centrifugal compressor', 'turbo compressor', 'dynamic compressor', 'multistage compressor', 'barrel compressor'],
    functions: [
      { description: 'Compress gas from suction to discharge at required pressure ratio', type: 'Primary', performanceStandard: 'Rated flow ±5% at design discharge pressure' },
      { description: 'Contain process gas without leakage', type: 'Primary', performanceStandard: 'Zero external leakage per API 617' },
      { description: 'Operate within surge/choke limits', type: 'Protective', performanceStandard: 'Anti-surge controller maintains >10% surge margin' },
    ],
    failuremodes: [
      { description: 'Surge — unstable operation with flow reversal', mechanism: 'Aerodynamic instability', cause: 'Low flow, fouled impeller, system resistance change', severity: 9, detectionMethod: 'Anti-surge controller, vibration spike' },
      { description: 'Journal bearing failure', mechanism: 'Oil film breakdown', cause: 'Oil contamination, overload, misalignment', severity: 8, detectionMethod: 'Bearing temperature, vibration monitoring' },
      { description: 'Impeller erosion/fouling', mechanism: 'Particulate erosion, polymer deposit', cause: 'Dirty gas, polymerisation', severity: 5, detectionMethod: 'Performance degradation, head drop' },
      { description: 'Dry gas seal failure', mechanism: 'Seal face damage', cause: 'Contaminated seal gas, pressure excursion', severity: 7, detectionMethod: 'Seal leakage rate monitoring' },
    ],
    scheduledtasks: [
      { description: 'Vibration monitoring — rotor condition', interval: 168, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Performance monitoring — head/flow/power', interval: 720, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Dry gas seal leakage check', interval: 720, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Lube oil analysis', interval: 2160, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Major overhaul — rotor, seals, bearings', interval: 35040, intervalUnit: 'hours', strategy: 'Fixed Time' },
    ],
    consequences: [
      { description: 'Loss of gas compression — plant shutdown', category: 'Operational', severity: 9, likelihood: 3 },
      { description: 'Hydrocarbon gas release', category: 'Safety', severity: 8, likelihood: 2 },
    ]
  },

  'Screw Compressor': {
    category: 'Rotating', standard: 'ISO 14224 §A.4',
    tags: ['screw compressor', 'rotary screw', 'twin screw', 'oil-injected', 'oil-free compressor'],
    functions: [
      { description: 'Compress gas/air to required delivery pressure', type: 'Primary', performanceStandard: 'Rated capacity ±5% at rated discharge pressure' },
      { description: 'Deliver clean dry compressed air/gas', type: 'Primary', performanceStandard: 'ISO 8573 Class specification met' },
    ],
    failuremodes: [
      { description: 'Rotor wear — reduced efficiency', mechanism: 'Erosion of rotor clearances', cause: 'Contaminated inlet, age, overload', severity: 5, detectionMethod: 'Power monitoring, discharge temperature rise' },
      { description: 'Oil separator failure — carryover', mechanism: 'Separator element degradation', cause: 'Element age, wrong oil type', severity: 4, detectionMethod: 'Oil carryover measurement' },
      { description: 'Bearing failure', mechanism: 'Rolling element fatigue', cause: 'Lubrication failure, overload', severity: 7, detectionMethod: 'Vibration analysis, temperature' },
      { description: 'Inlet valve stuck — no load/load cycling', mechanism: 'Valve seizure', cause: 'Carbon build-up, spring failure', severity: 4, detectionMethod: 'Load/unload monitoring' },
    ],
    scheduledtasks: [
      { description: 'Oil and filter change', interval: 4000, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Air/oil separator element replacement', interval: 8000, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Inlet valve overhaul', interval: 16000, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Vibration survey', interval: 2160, intervalUnit: 'hours', strategy: 'Condition Based' },
    ],
    consequences: [
      { description: 'Loss of compressed air — instrument air failure', category: 'Operational', severity: 8, likelihood: 3 },
    ]
  },

  'Reciprocating Compressor': {
    category: 'Rotating', standard: 'ISO 14224 §A.4',
    tags: ['reciprocating compressor', 'recip compressor', 'piston compressor', 'cylinder', 'crosshead'],
    functions: [
      { description: 'Compress gas to required discharge pressure in stages', type: 'Primary', performanceStandard: 'Rated capacity at rated pressure ratio per stage' },
      { description: 'Contain process gas within cylinder and packing', type: 'Primary', performanceStandard: 'Packing leakage < API 618 limits' },
    ],
    failuremodes: [
      { description: 'Valve failure — broken plate/spring', mechanism: 'Fatigue fracture', cause: 'Liquid carryover, high cycle fatigue', severity: 6, detectionMethod: 'Valve temperature monitoring, ultrasonic' },
      { description: 'Piston ring wear — loss of compression', mechanism: 'Ring groove wear', cause: 'Abrasive gas, misalignment, age', severity: 5, detectionMethod: 'Performance test, temperature differential' },
      { description: 'Rod packing leak', mechanism: 'Packing ring wear', cause: 'Rod scoring, age, thermal cycling', severity: 6, detectionMethod: 'Packing vent flow measurement' },
      { description: 'Crosshead/wrist pin failure', mechanism: 'Bearing wear, looseness', cause: 'Liquid slugging, overload', severity: 8, detectionMethod: 'Rod drop measurement, vibration' },
    ],
    scheduledtasks: [
      { description: 'Valve temperature monitoring', interval: 168, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Rod drop measurement', interval: 720, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Packing vent flow check', interval: 720, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Valve overhaul', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Piston ring/packing replacement', interval: 17520, intervalUnit: 'hours', strategy: 'Fixed Time' },
    ],
    consequences: [
      { description: 'Loss of gas compression — process interruption', category: 'Operational', severity: 8, likelihood: 3 },
      { description: 'Hydrocarbon release from packing', category: 'Environmental', severity: 6, likelihood: 4 },
    ]
  },

  'Diesel Engine': {
    category: 'Rotating', standard: 'ISO 14224 §A.6',
    tags: ['diesel engine', 'engine', 'prime mover', 'internal combustion', 'standby engine', 'fire pump engine'],
    functions: [
      { description: 'Provide mechanical drive power on demand', type: 'Primary', performanceStandard: 'Rated kW output within ±5% at rated speed' },
      { description: 'Start and reach rated speed within specified time', type: 'Primary', performanceStandard: 'Start to rated speed < 10 seconds (emergency), < 30 seconds (normal)' },
    ],
    failuremodes: [
      { description: 'Failure to start', mechanism: 'Starting system fault', cause: 'Dead battery, starter motor failure, fuel supply issue', severity: 9, detectionMethod: 'Periodic start test' },
      { description: 'Overheating — coolant system failure', mechanism: 'Cooling system degradation', cause: 'Coolant leak, thermostat failure, radiator fouling', severity: 7, detectionMethod: 'Temperature monitoring' },
      { description: 'Fuel injection failure', mechanism: 'Injector wear/blockage', cause: 'Contaminated fuel, injector erosion', severity: 6, detectionMethod: 'Exhaust temperature spread, smoke' },
      { description: 'Turbocharger failure', mechanism: 'Bearing failure, blade damage', cause: 'Oil starvation, foreign object', severity: 7, detectionMethod: 'Boost pressure drop, vibration' },
    ],
    scheduledtasks: [
      { description: 'Weekly start and run test', interval: 168, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Oil and filter change', interval: 500, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Coolant system check', interval: 2160, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Injector overhaul', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Major overhaul — pistons, liners, valves', interval: 35040, intervalUnit: 'hours', strategy: 'Fixed Time' },
    ],
    consequences: [
      { description: 'Emergency power not available', category: 'Safety', severity: 9, likelihood: 2 },
      { description: 'Fire pump not available — fire risk', category: 'Safety', severity: 10, likelihood: 2 },
    ]
  },

  'Hydraulic Power Unit': {
    category: 'Rotating', standard: 'ISO 14224 §A.12',
    tags: ['hpu', 'hydraulic power unit', 'hydraulic pump', 'hydraulic system', 'accumulator system'],
    functions: [
      { description: 'Supply hydraulic fluid at required pressure and flow', type: 'Primary', performanceStandard: 'Rated pressure ±2%, rated flow ±5%' },
      { description: 'Maintain hydraulic accumulator pre-charge', type: 'Protective', performanceStandard: 'Accumulator pressure > minimum trip value' },
    ],
    failuremodes: [
      { description: 'Pump wear — low pressure', mechanism: 'Internal clearance increase', cause: 'Contaminated fluid, cavitation', severity: 6, detectionMethod: 'Pressure monitoring, flow test' },
      { description: 'Hydraulic fluid leak', mechanism: 'Hose/fitting failure', cause: 'Vibration fatigue, age, over-pressure', severity: 5, detectionMethod: 'Level monitoring, visual inspection' },
      { description: 'Fluid contamination', mechanism: 'Particle ingress, water contamination', cause: 'Failed filter, seal leak, condensation', severity: 5, detectionMethod: 'Oil analysis, particle counting' },
    ],
    scheduledtasks: [
      { description: 'Hydraulic fluid analysis', interval: 2160, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Filter element change', interval: 4380, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Accumulator pre-charge check', interval: 4380, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Hose and fitting inspection', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' },
    ],
    consequences: [
      { description: 'Loss of hydraulic supply — ESD valve inoperable', category: 'Safety', severity: 9, likelihood: 2 },
      { description: 'Environmental contamination from fluid spill', category: 'Environmental', severity: 5, likelihood: 3 },
    ]
  },

  'Agitator/Mixer': {
    category: 'Rotating', standard: 'ISO 14224 §A.14',
    tags: ['agitator', 'mixer', 'stirrer', 'blender', 'mixing vessel', 'reactor agitator'],
    functions: [
      { description: 'Provide homogeneous mixing of vessel contents', type: 'Primary', performanceStandard: 'Uniform concentration within 5% across vessel volume' },
      { description: 'Maintain process temperature through mixing', type: 'Primary', performanceStandard: 'Temperature gradient < 2°C within vessel' },
    ],
    failuremodes: [
      { description: 'Shaft seal leak', mechanism: 'Mechanical seal wear', cause: 'Corrosion, thermal cycling, misalignment', severity: 6, detectionMethod: 'Visual inspection, seal pressure monitoring' },
      { description: 'Impeller blade erosion/corrosion', mechanism: 'Chemical/mechanical degradation', cause: 'Corrosive media, abrasive solids', severity: 5, detectionMethod: 'Motor current trending, visual at shutdown' },
      { description: 'Gearbox failure', mechanism: 'Gear tooth wear, bearing failure', cause: 'Overload, oil degradation', severity: 7, detectionMethod: 'Vibration analysis, oil analysis' },
    ],
    scheduledtasks: [
      { description: 'Vibration monitoring', interval: 720, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Gearbox oil analysis', interval: 2160, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Seal inspection/replacement', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Impeller inspection at vessel entry', interval: 17520, intervalUnit: 'hours', strategy: 'Fixed Time' },
    ],
    consequences: [
      { description: 'Off-spec product — batch loss', category: 'Operational', severity: 6, likelihood: 4 },
      { description: 'Process fluid leak from shaft seal', category: 'Environmental', severity: 6, likelihood: 3 },
    ]
  },

  'Centrifuge': {
    category: 'Rotating', standard: 'ISO 14224 §A.15',
    tags: ['centrifuge', 'decanter', 'disc separator', 'separator centrifuge', 'liquid-liquid separator'],
    functions: [
      { description: 'Separate phases by centrifugal force', type: 'Primary', performanceStandard: 'Separation efficiency > 95% at rated throughput' },
    ],
    failuremodes: [
      { description: 'Unbalance — excessive vibration', mechanism: 'Uneven solids build-up', cause: 'Feed variation, nozzle blockage', severity: 7, detectionMethod: 'Vibration monitoring' },
      { description: 'Bowl/scroll wear', mechanism: 'Erosion', cause: 'Abrasive solids, high throughput', severity: 6, detectionMethod: 'Solids in centrate, vibration' },
      { description: 'Drive belt/coupling failure', mechanism: 'Fatigue, slippage', cause: 'Age, misalignment, overload', severity: 5, detectionMethod: 'Speed monitoring, visual' },
    ],
    scheduledtasks: [
      { description: 'Vibration monitoring', interval: 720, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Bowl clean and inspection', interval: 4380, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Drive belt/coupling inspection', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' },
    ],
    consequences: [
      { description: 'Product quality — poor separation', category: 'Operational', severity: 6, likelihood: 4 },
    ]
  },

  'Wind Turbine': {
    category: 'Rotating', standard: 'IEC 61400',
    tags: ['wind turbine', 'wind generator', 'wtg', 'nacelle', 'wind farm'],
    functions: [
      { description: 'Convert wind energy to electrical power', type: 'Primary', performanceStandard: 'Power curve per IEC 61400-12 within ±5%' },
      { description: 'Operate safely in all wind conditions', type: 'Protective', performanceStandard: 'Emergency stop from any operating state < 5 seconds' },
    ],
    failuremodes: [
      { description: 'Gearbox failure', mechanism: 'Gear tooth wear, bearing failure', cause: 'Torque transients, lubrication breakdown', severity: 9, detectionMethod: 'Vibration analysis, oil analysis, SCADA' },
      { description: 'Blade damage — crack/erosion', mechanism: 'Leading edge erosion, structural fatigue', cause: 'Rain erosion, lightning, fatigue cycling', severity: 8, detectionMethod: 'Visual inspection, blade monitoring' },
      { description: 'Pitch system failure', mechanism: 'Pitch actuator/bearing failure', cause: 'Hydraulic leak, bearing seizure, encoder fault', severity: 8, detectionMethod: 'Pitch angle monitoring, SCADA alarm' },
      { description: 'Generator bearing failure', mechanism: 'Rolling element fatigue', cause: 'Current passage, lubrication, misalignment', severity: 7, detectionMethod: 'Vibration, temperature monitoring' },
    ],
    scheduledtasks: [
      { description: 'Remote SCADA monitoring — performance trending', interval: 24, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Gearbox oil analysis', interval: 2160, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Annual service — bolts, fluids, filters', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Blade inspection (rope access/drone)', interval: 17520, intervalUnit: 'hours', strategy: 'Fixed Time' },
    ],
    consequences: [
      { description: 'Lost energy production revenue', category: 'Operational', severity: 7, likelihood: 4 },
      { description: 'Blade throw / tower collapse risk', category: 'Safety', severity: 10, likelihood: 1 },
    ]
  },

  // =========================================================================
  // ISO 14224 — STATIC EQUIPMENT (Additional)
  // =========================================================================
  'Distillation Column': {
    category: 'Static', standard: 'ISO 14224 §A.20',
    tags: ['column', 'distillation', 'fractionator', 'stripping column', 'absorber', 'scrubber', 'tray column', 'packed column'],
    functions: [
      { description: 'Separate feed into overhead and bottoms products at specified purity', type: 'Primary', performanceStandard: 'Product specifications within ±0.5% composition' },
      { description: 'Contain process fluids at design pressure/temperature', type: 'Primary', performanceStandard: 'Zero external leakage, within MAWP envelope' },
    ],
    failuremodes: [
      { description: 'Tray/packing fouling — reduced separation', mechanism: 'Deposit build-up', cause: 'Polymerisation, corrosion products, foaming', severity: 5, detectionMethod: 'Pressure drop increase, product quality' },
      { description: 'Corrosion under insulation', mechanism: 'External corrosion', cause: 'Moisture ingress, insulation damage', severity: 7, detectionMethod: 'CUI inspection, UT thickness' },
      { description: 'Nozzle/weld cracking', mechanism: 'Stress corrosion cracking', cause: 'H2S, chlorides, caustic, high stress', severity: 8, detectionMethod: 'MPI/UT at nozzle welds' },
    ],
    scheduledtasks: [
      { description: 'Product quality monitoring', interval: 8, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Pressure drop trending', interval: 720, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'External visual/CUI inspection', interval: 26280, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Internal inspection at turnaround', interval: 43800, intervalUnit: 'hours', strategy: 'Fixed Time' },
    ],
    consequences: [
      { description: 'Off-spec product — reprocessing required', category: 'Operational', severity: 6, likelihood: 4 },
      { description: 'Vessel failure — hydrocarbon release', category: 'Safety', severity: 10, likelihood: 1 },
    ]
  },

  'Flare System': {
    category: 'Static', standard: 'ISO 14224 §A.25',
    tags: ['flare', 'flare stack', 'flare tip', 'flare header', 'knockout drum', 'flare pilot'],
    functions: [
      { description: 'Safely combust relieved gases to atmosphere', type: 'Safety', performanceStandard: 'Continuous pilot flame, 98% combustion efficiency' },
      { description: 'Accept emergency relief from all connected systems', type: 'Safety', performanceStandard: 'Design capacity sufficient for simultaneous relief' },
    ],
    failuremodes: [
      { description: 'Pilot flame out — unignited release', mechanism: 'Wind extinguishment, fuel supply loss', cause: 'High wind, fuel gas pressure loss, igniter failure', severity: 9, detectionMethod: 'Pilot flame detection, thermocouple' },
      { description: 'Liquid carryover to flare', mechanism: 'Knockout drum level high', cause: 'Excessive relief, level control failure', severity: 8, detectionMethod: 'KO drum level monitoring' },
      { description: 'Flare tip degradation', mechanism: 'High temperature erosion/oxidation', cause: 'Extended high-rate flaring, material degradation', severity: 5, detectionMethod: 'Visual inspection (drone), IR survey' },
    ],
    scheduledtasks: [
      { description: 'Pilot flame monitoring', interval: 8, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'KO drum level check and drain', interval: 168, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Flare tip visual inspection (drone)', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Flare header integrity inspection', interval: 43800, intervalUnit: 'hours', strategy: 'Fixed Time' },
    ],
    consequences: [
      { description: 'Unignited hydrocarbon release', category: 'Environmental', severity: 9, likelihood: 2 },
      { description: 'Ground-level flammable cloud', category: 'Safety', severity: 10, likelihood: 1 },
    ]
  },

  'Loading Arm': {
    category: 'Static', standard: 'ISO 14224',
    tags: ['loading arm', 'marine loading', 'tanker loading', 'chicsan', 'swivel joint'],
    functions: [
      { description: 'Transfer fluid between facility and transport vessel', type: 'Primary', performanceStandard: 'Rated flow rate with zero external leakage' },
      { description: 'Emergency disconnect from transport vessel', type: 'Protective', performanceStandard: 'Emergency release < 30 seconds' },
    ],
    failuremodes: [
      { description: 'Swivel joint seal leak', mechanism: 'Seal wear', cause: 'Age, misalignment, corrosion', severity: 7, detectionMethod: 'Visual inspection, drip pan monitoring' },
      { description: 'Emergency release coupling failure', mechanism: 'Hydraulic/mechanical failure', cause: 'Corrosion, hydraulic leak, mechanical seizure', severity: 9, detectionMethod: 'Function test' },
    ],
    scheduledtasks: [
      { description: 'Swivel joint leak check', interval: 720, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Emergency release coupling test', interval: 4380, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Full swivel joint overhaul', interval: 43800, intervalUnit: 'hours', strategy: 'Fixed Time' },
    ],
    consequences: [
      { description: 'Hydrocarbon spill during loading', category: 'Environmental', severity: 9, likelihood: 2 },
      { description: 'Vessel breakaway with arm connected', category: 'Safety', severity: 10, likelihood: 1 },
    ]
  },

  'Gasket/Flange Assembly': {
    category: 'Static', standard: 'ASME PCC-1',
    tags: ['gasket', 'flange', 'bolted joint', 'bolted flange', 'ring joint', 'spiral wound'],
    functions: [
      { description: 'Maintain pressure-tight joint between piping/equipment', type: 'Primary', performanceStandard: 'Zero visible leakage per ASME PCC-1' },
    ],
    failuremodes: [
      { description: 'Gasket blowout — external leak', mechanism: 'Gasket extrusion/degradation', cause: 'Over-pressure, thermal cycling, incorrect installation', severity: 8, detectionMethod: 'LDAR, visual inspection, acoustic emission' },
      { description: 'Bolt relaxation — loss of clamp load', mechanism: 'Creep, embedment', cause: 'High temperature, insufficient initial torque', severity: 6, detectionMethod: 'Bolt tension monitoring, leak check' },
    ],
    scheduledtasks: [
      { description: 'LDAR leak survey', interval: 4380, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Hot bolt retorque (high-temperature service)', interval: 720, intervalUnit: 'hours', strategy: 'Fixed Time' },
    ],
    consequences: [
      { description: 'Fugitive emissions violation', category: 'Environmental', severity: 6, likelihood: 4 },
      { description: 'Flammable release — fire risk', category: 'Safety', severity: 9, likelihood: 2 },
    ]
  },

  'Expansion Joint': {
    category: 'Static', standard: 'EJMA Standards',
    tags: ['expansion joint', 'bellows', 'expansion bellows', 'compensator', 'flexible joint'],
    functions: [
      { description: 'Accommodate thermal movement in piping system', type: 'Primary', performanceStandard: 'Design movement range without leakage or distortion' },
    ],
    failuremodes: [
      { description: 'Bellows fatigue crack', mechanism: 'Cyclic fatigue', cause: 'Excessive cycling, over-extension, pressure pulsation', severity: 8, detectionMethod: 'Visual inspection, leak detection' },
      { description: 'Corrosion of convolutions', mechanism: 'External/internal corrosion', cause: 'Chlorides, moisture, acidic condensate', severity: 7, detectionMethod: 'Visual inspection, dye penetrant' },
    ],
    scheduledtasks: [
      { description: 'Visual inspection — bellows condition', interval: 4380, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Movement check — travel indicators', interval: 8760, intervalUnit: 'hours', strategy: 'Condition Based' },
    ],
    consequences: [
      { description: 'Catastrophic bellows rupture — uncontained release', category: 'Safety', severity: 9, likelihood: 2 },
    ]
  },

  // =========================================================================
  // ISO 14224 — ELECTRICAL (Additional)
  // =========================================================================
  'Power Cable': {
    category: 'Electrical', standard: 'IEC 60502',
    tags: ['cable', 'power cable', 'mv cable', 'hv cable', 'lv cable', 'cable termination', 'cable joint'],
    functions: [
      { description: 'Transmit electrical power from source to load', type: 'Primary', performanceStandard: 'Rated current without exceeding insulation temperature' },
      { description: 'Maintain insulation integrity', type: 'Primary', performanceStandard: 'Insulation resistance > minimum per IEC 60502' },
    ],
    failuremodes: [
      { description: 'Insulation failure — earth fault', mechanism: 'Insulation degradation', cause: 'Water treeing, thermal ageing, mechanical damage', severity: 7, detectionMethod: 'Partial discharge monitoring, insulation resistance test' },
      { description: 'Termination/joint failure', mechanism: 'Contact degradation', cause: 'Poor workmanship, thermal cycling, moisture ingress', severity: 7, detectionMethod: 'Thermography, partial discharge' },
    ],
    scheduledtasks: [
      { description: 'Thermographic survey — joints/terminations', interval: 8760, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Insulation resistance test', interval: 26280, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Partial discharge test (MV/HV)', interval: 26280, intervalUnit: 'hours', strategy: 'Condition Based' },
    ],
    consequences: [
      { description: 'Loss of power to critical load', category: 'Operational', severity: 8, likelihood: 3 },
      { description: 'Cable fire', category: 'Safety', severity: 8, likelihood: 2 },
    ]
  },

  'Capacitor Bank': {
    category: 'Electrical', standard: 'IEC 60871',
    tags: ['capacitor', 'capacitor bank', 'power factor correction', 'pfc', 'harmonic filter'],
    functions: [
      { description: 'Correct power factor to target value', type: 'Primary', performanceStandard: 'Power factor > 0.95 at rated load' },
    ],
    failuremodes: [
      { description: 'Capacitor can failure — rupture', mechanism: 'Dielectric breakdown', cause: 'Overvoltage, harmonics, manufacturing defect', severity: 7, detectionMethod: 'Capacitance measurement, protection relay' },
      { description: 'Contactor failure', mechanism: 'Contact welding/erosion', cause: 'Inrush current, age', severity: 5, detectionMethod: 'Switching function test' },
    ],
    scheduledtasks: [
      { description: 'Thermographic survey', interval: 8760, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Capacitance measurement', interval: 17520, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Contactor inspection/replacement', interval: 17520, intervalUnit: 'hours', strategy: 'Fixed Time' },
    ],
    consequences: [
      { description: 'Power factor penalty charges', category: 'Operational', severity: 4, likelihood: 4 },
      { description: 'Capacitor rupture — arc flash', category: 'Safety', severity: 8, likelihood: 2 },
    ]
  },

  'Earthing/Grounding System': {
    category: 'Electrical', standard: 'IEEE 80/BS 7430',
    tags: ['earthing', 'grounding', 'earth electrode', 'ground grid', 'lightning protection', 'cathodic protection'],
    functions: [
      { description: 'Provide low-impedance fault current path to earth', type: 'Safety', performanceStandard: 'Earth resistance < 1 ohm (substations), < 10 ohm (buildings)' },
    ],
    failuremodes: [
      { description: 'Earth resistance increase — degraded protection', mechanism: 'Electrode corrosion', cause: 'Soil corrosion, soil drying, connection degradation', severity: 7, detectionMethod: 'Earth resistance test' },
      { description: 'Bonding connection failure', mechanism: 'Corrosion of conductor/clamp', cause: 'Galvanic corrosion, mechanical damage', severity: 6, detectionMethod: 'Continuity test, visual inspection' },
    ],
    scheduledtasks: [
      { description: 'Earth resistance measurement', interval: 8760, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Bonding continuity check', interval: 17520, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Visual inspection of exposed conductors', interval: 8760, intervalUnit: 'hours', strategy: 'Condition Based' },
    ],
    consequences: [
      { description: 'Touch/step potential hazard', category: 'Safety', severity: 9, likelihood: 2 },
      { description: 'Equipment damage from fault current', category: 'Operational', severity: 7, likelihood: 3 },
    ]
  },

  'Emergency Lighting': {
    category: 'Electrical', standard: 'BS 5266 / SFG20 SL-02',
    tags: ['emergency lighting', 'exit sign', 'emergency luminaire', 'self-contained luminaire', 'central battery'],
    functions: [
      { description: 'Provide illumination on mains failure for safe evacuation', type: 'Safety', performanceStandard: 'Minimum 1 lux on escape routes for 3 hours' },
    ],
    failuremodes: [
      { description: 'Battery failure — insufficient duration', mechanism: 'Battery capacity loss', cause: 'Age, temperature, deep discharge', severity: 7, detectionMethod: 'Duration test' },
      { description: 'Lamp/LED failure', mechanism: 'Driver or LED degradation', cause: 'Age, thermal stress', severity: 4, detectionMethod: 'Functional test, visual inspection' },
    ],
    scheduledtasks: [
      { description: 'Monthly function test (30-second)', interval: 720, intervalUnit: 'hours', strategy: 'Fixed Time', sfg20Ref: 'SFG20 SL-02' },
      { description: 'Annual full duration test (3-hour)', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time', sfg20Ref: 'SFG20 SL-02' },
    ],
    consequences: [
      { description: 'Evacuation route not illuminated', category: 'Safety', severity: 8, likelihood: 3 },
    ]
  },

  'Lightning Protection System': {
    category: 'Electrical', standard: 'BS EN 62305 / SFG20 SL-01',
    tags: ['lightning protection', 'lps', 'air terminal', 'down conductor', 'strike termination'],
    functions: [
      { description: 'Intercept lightning strikes and conduct current safely to earth', type: 'Safety', performanceStandard: 'Protection level per BS EN 62305 risk assessment class' },
    ],
    failuremodes: [
      { description: 'Down conductor corrosion/break', mechanism: 'Environmental corrosion', cause: 'Weather exposure, dissimilar metals', severity: 7, detectionMethod: 'Visual inspection, continuity test' },
      { description: 'Earth termination resistance increase', mechanism: 'Electrode corrosion', cause: 'Soil conditions, age', severity: 6, detectionMethod: 'Earth resistance test' },
    ],
    scheduledtasks: [
      { description: 'Visual inspection of air terminals and down conductors', interval: 8760, intervalUnit: 'hours', strategy: 'Condition Based', sfg20Ref: 'SFG20 SL-01' },
      { description: 'Continuity and earth resistance test', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time', sfg20Ref: 'SFG20 SL-01' },
    ],
    consequences: [
      { description: 'Lightning damage to structure/equipment', category: 'Safety', severity: 8, likelihood: 2 },
    ]
  },

  // =========================================================================
  // ISO 14224 — CONTROL & SAFETY (Additional)
  // =========================================================================
  'Pressure Safety Valve': {
    category: 'Control & Safety', standard: 'API 520/521/526',
    tags: ['psv', 'safety valve', 'relief valve', 'prv', 'pressure relief', 'safety relief valve'],
    functions: [
      { description: 'Relieve overpressure to protect equipment from exceeding MAWP', type: 'Safety', performanceStandard: 'Open at set pressure ±3%, reseat at blowdown pressure' },
    ],
    failuremodes: [
      { description: 'Fail to open at set pressure — stuck closed', mechanism: 'Seat bonding, corrosion, spring failure', cause: 'Process deposits, corrosion, infrequent operation', severity: 10, detectionMethod: 'Pop test, acoustic monitoring' },
      { description: 'Leaking through — premature lift', mechanism: 'Seat damage', cause: 'Chatter, previous lift, foreign material', severity: 4, detectionMethod: 'Acoustic monitoring, process indication' },
      { description: 'Fail to reseat — continuous relief', mechanism: 'Seat damage from chatter', cause: 'Incorrect sizing, inlet pressure drop', severity: 5, detectionMethod: 'Temperature monitoring, noise' },
    ],
    scheduledtasks: [
      { description: 'Pop test — verify set pressure', interval: 17520, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Acoustic emission monitoring', interval: 4380, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Full overhaul — seat lapping, spring calibration', interval: 43800, intervalUnit: 'hours', strategy: 'Fixed Time' },
    ],
    consequences: [
      { description: 'Vessel overpressure — catastrophic failure', category: 'Safety', severity: 10, likelihood: 1 },
      { description: 'Continuous flaring — emissions violation', category: 'Environmental', severity: 5, likelihood: 3 },
    ]
  },

  'Flow Meter': {
    category: 'Control & Safety', standard: 'ISO 14224 §A.32',
    tags: ['flow meter', 'orifice plate', 'coriolis', 'ultrasonic meter', 'vortex meter', 'turbine meter', 'mag meter'],
    functions: [
      { description: 'Measure flow rate with specified accuracy', type: 'Primary', performanceStandard: 'Measurement accuracy per API MPMS / ISO 5167' },
    ],
    failuremodes: [
      { description: 'Measurement drift — inaccurate reading', mechanism: 'Sensor degradation', cause: 'Erosion, fouling, electronics drift', severity: 5, detectionMethod: 'Calibration check, comparison with secondary measurement' },
      { description: 'Signal loss — no reading', mechanism: 'Transmitter failure', cause: 'Power supply, electronics failure, wiring fault', severity: 6, detectionMethod: 'Signal diagnostics, SCADA alarm' },
    ],
    scheduledtasks: [
      { description: 'Calibration verification', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Orifice plate inspection (if applicable)', interval: 17520, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Transmitter diagnostics check', interval: 4380, intervalUnit: 'hours', strategy: 'Condition Based' },
    ],
    consequences: [
      { description: 'Fiscal metering error — financial loss', category: 'Operational', severity: 7, likelihood: 3 },
      { description: 'Process control upset from erroneous reading', category: 'Operational', severity: 5, likelihood: 4 },
    ]
  },

  'Level Instrument': {
    category: 'Control & Safety', standard: 'ISO 14224 §A.33',
    tags: ['level gauge', 'level transmitter', 'level switch', 'level instrument', 'radar level', 'guided wave', 'displacer', 'float switch'],
    functions: [
      { description: 'Measure or detect level within vessel/tank', type: 'Primary', performanceStandard: 'Accuracy ±1% of span' },
    ],
    failuremodes: [
      { description: 'False level indication — measurement error', mechanism: 'Sensor fouling/blockage', cause: 'Process deposits, condensation, emulsion layer', severity: 6, detectionMethod: 'Comparison with independent measurement' },
      { description: 'Level switch fail to operate', mechanism: 'Mechanical sticking', cause: 'Process deposits, corrosion', severity: 7, detectionMethod: 'Function test' },
    ],
    scheduledtasks: [
      { description: 'Function test — level switch', interval: 4380, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Calibration check', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Bridle/chamber clean (if applicable)', interval: 17520, intervalUnit: 'hours', strategy: 'Fixed Time' },
    ],
    consequences: [
      { description: 'Vessel overflow/dry run', category: 'Operational', severity: 7, likelihood: 3 },
      { description: 'High level trip not activated', category: 'Safety', severity: 9, likelihood: 2 },
    ]
  },

  'Temperature Instrument': {
    category: 'Control & Safety', standard: 'ISO 14224',
    tags: ['thermocouple', 'rtd', 'temperature element', 'temperature transmitter', 'pyrometer', 'thermowell'],
    functions: [
      { description: 'Measure temperature at process point', type: 'Primary', performanceStandard: 'Accuracy per IEC 60751 (RTD) / IEC 60584 (TC)' },
    ],
    failuremodes: [
      { description: 'Drift — reading out of tolerance', mechanism: 'Sensor degradation', cause: 'Metallurgical change, insulation breakdown, age', severity: 4, detectionMethod: 'Calibration check, comparison' },
      { description: 'Open circuit — signal loss', mechanism: 'Element break', cause: 'Thermal shock, vibration fatigue, corrosion', severity: 5, detectionMethod: 'Signal diagnostics' },
      { description: 'Thermowell failure', mechanism: 'Fatigue crack, erosion', cause: 'Vortex-induced vibration, high velocity, corrosion', severity: 7, detectionMethod: 'UT/visual at shutdown' },
    ],
    scheduledtasks: [
      { description: 'Calibration verification', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Thermowell inspection', interval: 43800, intervalUnit: 'hours', strategy: 'Fixed Time' },
    ],
    consequences: [
      { description: 'Process control upset from inaccurate reading', category: 'Operational', severity: 5, likelihood: 4 },
    ]
  },

  'Pressure Instrument': {
    category: 'Control & Safety', standard: 'ISO 14224',
    tags: ['pressure gauge', 'pressure transmitter', 'pressure switch', 'pressure indicator', 'manometer', 'bourdon tube'],
    functions: [
      { description: 'Measure pressure at process point', type: 'Primary', performanceStandard: 'Accuracy ±0.5% of calibrated range' },
    ],
    failuremodes: [
      { description: 'Drift — reading inaccurate', mechanism: 'Sensor/diaphragm degradation', cause: 'Overpressure, vibration, process attack', severity: 5, detectionMethod: 'Calibration check' },
      { description: 'Impulse line blockage', mechanism: 'Process deposit in sensing line', cause: 'Wax, hydrate, corrosion product', severity: 5, detectionMethod: 'Comparison, response time test' },
    ],
    scheduledtasks: [
      { description: 'Calibration verification', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Impulse line blow-through', interval: 4380, intervalUnit: 'hours', strategy: 'Fixed Time' },
    ],
    consequences: [
      { description: 'Erroneous pressure indication — control upset', category: 'Operational', severity: 5, likelihood: 4 },
    ]
  },

  'Gas Detection System': {
    category: 'Control & Safety', standard: 'IEC 60079-29',
    tags: ['gas detector', 'flammable gas detector', 'toxic gas detector', 'h2s detector', 'lel detector', 'open path detector', 'point detector'],
    functions: [
      { description: 'Detect flammable/toxic gas and initiate alarm/action', type: 'Safety', performanceStandard: 'Detect at alarm threshold within T90 response time' },
    ],
    failuremodes: [
      { description: 'Loss of sensitivity — fail to detect', mechanism: 'Sensor poisoning/degradation', cause: 'Silicone contamination, H2S poisoning, age', severity: 9, detectionMethod: 'Bump test, calibration check' },
      { description: 'False alarm — spurious detection', mechanism: 'Cross-sensitivity, environmental interference', cause: 'Water ingress, temperature change, interfering gas', severity: 4, detectionMethod: 'Alarm log analysis' },
    ],
    scheduledtasks: [
      { description: 'Bump test — response verification', interval: 720, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Full calibration', interval: 4380, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Sensor element replacement', interval: 17520, intervalUnit: 'hours', strategy: 'Fixed Time' },
    ],
    consequences: [
      { description: 'Undetected gas cloud — explosion/toxic exposure', category: 'Safety', severity: 10, likelihood: 1 },
    ]
  },

  'Fire Damper': {
    category: 'Control & Safety', standard: 'BS EN 15650 / SFG20 FD-01',
    tags: ['fire damper', 'smoke damper', 'combination damper', 'intumescent damper'],
    functions: [
      { description: 'Close on fire detection to prevent spread of fire/smoke through ductwork', type: 'Safety', performanceStandard: 'Close within 60 seconds of activation, rated fire resistance' },
    ],
    failuremodes: [
      { description: 'Fail to close — seized mechanism', mechanism: 'Corrosion, binding', cause: 'Lack of maintenance, duct debris, corrosion', severity: 9, detectionMethod: 'Drop test' },
      { description: 'Incomplete closure — gap', mechanism: 'Blade distortion, obstruction', cause: 'Physical damage, debris in duct', severity: 7, detectionMethod: 'Visual inspection, drop test' },
    ],
    scheduledtasks: [
      { description: 'Annual drop test', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time', sfg20Ref: 'SFG20 FD-01' },
      { description: 'Visual inspection and clean', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time', sfg20Ref: 'SFG20 FD-01' },
    ],
    consequences: [
      { description: 'Fire/smoke spread through ductwork between compartments', category: 'Safety', severity: 9, likelihood: 2 },
    ]
  },

  'Actuator (Electric)': {
    category: 'Control & Safety', standard: 'IEC 61491',
    tags: ['electric actuator', 'moa', 'motorised actuator', 'actuator', 'valve actuator'],
    functions: [
      { description: 'Position valve/damper to required setpoint on demand', type: 'Primary', performanceStandard: 'Stroke time within specification, position accuracy ±2%' },
    ],
    failuremodes: [
      { description: 'Motor failure — actuator inoperable', mechanism: 'Winding insulation failure', cause: 'Overheating, moisture ingress, age', severity: 7, detectionMethod: 'Function test, insulation resistance' },
      { description: 'Gearbox seizure', mechanism: 'Grease hardening, gear wear', cause: 'Lack of lubrication, contamination', severity: 6, detectionMethod: 'Torque monitoring, function test' },
      { description: 'Position feedback failure', mechanism: 'Potentiometer/encoder failure', cause: 'Age, vibration, moisture', severity: 5, detectionMethod: 'Position signal check' },
    ],
    scheduledtasks: [
      { description: 'Stroke test — full open/close', interval: 4380, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Torque measurement — trending', interval: 8760, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Gearbox lubrication', interval: 17520, intervalUnit: 'hours', strategy: 'Fixed Time' },
    ],
    consequences: [
      { description: 'Valve stuck — process control loss', category: 'Operational', severity: 7, likelihood: 3 },
    ]
  },

  'Actuator (Pneumatic)': {
    category: 'Control & Safety', standard: 'ISA 75.05',
    tags: ['pneumatic actuator', 'diaphragm actuator', 'piston actuator', 'spring-return actuator'],
    functions: [
      { description: 'Position valve to control signal setpoint', type: 'Primary', performanceStandard: 'Stroke time and dead band per ISA 75.05' },
      { description: 'Fail-safe on air supply loss', type: 'Protective', performanceStandard: 'Fail-open or fail-closed per design within 60 seconds' },
    ],
    failuremodes: [
      { description: 'Diaphragm rupture — loss of actuator force', mechanism: 'Diaphragm fatigue', cause: 'Age, over-pressure, chemical attack', severity: 7, detectionMethod: 'Air supply consumption monitoring' },
      { description: 'Spring failure — wrong fail position', mechanism: 'Spring fatigue', cause: 'Cycling, corrosion', severity: 8, detectionMethod: 'Partial stroke test' },
    ],
    scheduledtasks: [
      { description: 'Partial stroke test', interval: 4380, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Diaphragm inspection/replacement', interval: 43800, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Air supply check — regulator, filter', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' },
    ],
    consequences: [
      { description: 'Valve stuck — process upset', category: 'Operational', severity: 7, likelihood: 3 },
      { description: 'Safety valve fails to close/open', category: 'Safety', severity: 9, likelihood: 2 },
    ]
  },

  'SCADA/DCS System': {
    category: 'Control & Safety', standard: 'IEC 62443',
    tags: ['scada', 'dcs', 'distributed control', 'control system', 'operator interface', 'hmi', 'ois'],
    functions: [
      { description: 'Monitor and control process operations from central location', type: 'Primary', performanceStandard: 'System availability > 99.9%, scan cycle < 1 second' },
    ],
    failuremodes: [
      { description: 'Controller processor failure', mechanism: 'Electronics failure', cause: 'Component age, power disturbance, heat', severity: 8, detectionMethod: 'Redundancy switchover alarm, watchdog' },
      { description: 'Communication network failure', mechanism: 'Switch/cable fault', cause: 'Component failure, EMI, physical damage', severity: 7, detectionMethod: 'Network monitoring, packet loss alarm' },
      { description: 'HMI/operator station failure', mechanism: 'Hardware/software failure', cause: 'Disk failure, OS crash, hardware age', severity: 5, detectionMethod: 'Redundant station available' },
    ],
    scheduledtasks: [
      { description: 'System backup', interval: 168, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Security patch assessment and deployment', interval: 2160, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'UPS battery test', interval: 4380, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Hardware lifecycle review', interval: 17520, intervalUnit: 'hours', strategy: 'Condition Based' },
    ],
    consequences: [
      { description: 'Loss of process visibility — manual operation required', category: 'Operational', severity: 8, likelihood: 2 },
    ]
  },

  // =========================================================================
  // ISO 14224 — SUBSEA & MARINE
  // =========================================================================
  'Subsea Christmas Tree': {
    category: 'Subsea', standard: 'API 17D',
    tags: ['christmas tree', 'xmas tree', 'subsea tree', 'wellhead', 'tubing hanger'],
    functions: [
      { description: 'Control well flow and provide barrier between reservoir and environment', type: 'Primary', performanceStandard: 'Full bore access, pressure rated to wellhead design' },
      { description: 'Enable well shut-in on command', type: 'Safety', performanceStandard: 'Valve closure < 45 seconds on ESD command' },
    ],
    failuremodes: [
      { description: 'Valve fail to close — loss of barrier', mechanism: 'Hydraulic actuator failure, debris on seat', cause: 'Hydraulic leak, sand erosion, scale', severity: 10, detectionMethod: 'Valve position monitoring, leak rate test' },
      { description: 'Choke erosion — uncontrolled flow', mechanism: 'Sand erosion of choke trim', cause: 'Sand production, high velocity', severity: 8, detectionMethod: 'Choke differential monitoring' },
      { description: 'Hydraulic supply leak — loss of control', mechanism: 'Umbilical/connector leak', cause: 'Fatigue, corrosion, fishing damage', severity: 8, detectionMethod: 'Hydraulic pressure monitoring' },
    ],
    scheduledtasks: [
      { description: 'Valve function test — full stroke', interval: 4380, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Valve leak rate test', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'ROV visual inspection', interval: 8760, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Choke condition assessment', interval: 4380, intervalUnit: 'hours', strategy: 'Condition Based' },
    ],
    consequences: [
      { description: 'Uncontrolled well flow — environmental catastrophe', category: 'Environmental', severity: 10, likelihood: 1 },
      { description: 'Well shut-in — production loss', category: 'Operational', severity: 8, likelihood: 3 },
    ]
  },

  'Subsea Umbilical': {
    category: 'Subsea', standard: 'API 17E',
    tags: ['umbilical', 'subsea umbilical', 'hydraulic line', 'electrical flying lead', 'control umbilical'],
    functions: [
      { description: 'Transmit hydraulic power, electrical power, and signals to subsea equipment', type: 'Primary', performanceStandard: 'Maintain pressure and signal integrity over design life' },
    ],
    failuremodes: [
      { description: 'Hydraulic tube leak — loss of control', mechanism: 'Fatigue, corrosion', cause: 'Cyclic loading, installation damage, H2S', severity: 8, detectionMethod: 'Pressure monitoring, leak detection' },
      { description: 'Electrical core failure — signal loss', mechanism: 'Insulation breakdown', cause: 'Water ingress, mechanical damage', severity: 7, detectionMethod: 'Insulation resistance test, signal monitoring' },
    ],
    scheduledtasks: [
      { description: 'Pressure and signal integrity monitoring', interval: 720, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'ROV visual inspection — touchdown zone', interval: 8760, intervalUnit: 'hours', strategy: 'Condition Based' },
    ],
    consequences: [
      { description: 'Loss of subsea control — well shut-in required', category: 'Operational', severity: 8, likelihood: 3 },
    ]
  },

  'Riser': {
    category: 'Subsea', standard: 'API 2RD',
    tags: ['riser', 'production riser', 'drilling riser', 'flexible riser', 'steel catenary riser', 'scr'],
    functions: [
      { description: 'Transport fluids between seabed and surface facility', type: 'Primary', performanceStandard: 'Pressure and structural integrity over design life in fatigue' },
    ],
    failuremodes: [
      { description: 'Fatigue crack — structural failure', mechanism: 'Fatigue at stress concentration', cause: 'Wave/current loading, VIV, touch-down cycling', severity: 10, detectionMethod: 'Strain monitoring, FEA, inspection' },
      { description: 'Corrosion/erosion — wall loss', mechanism: 'Internal/external corrosion', cause: 'CO2, H2S, seawater, sand production', severity: 8, detectionMethod: 'Intelligent pigging, UT thickness' },
    ],
    scheduledtasks: [
      { description: 'Fatigue life monitoring', interval: 720, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'CP survey — external corrosion', interval: 8760, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Intelligent pig run (if piggable)', interval: 43800, intervalUnit: 'hours', strategy: 'Condition Based' },
    ],
    consequences: [
      { description: 'Riser failure — major hydrocarbon release', category: 'Safety', severity: 10, likelihood: 1 },
    ]
  },

  'Subsea Pipeline': {
    category: 'Subsea', standard: 'DNV-ST-F101',
    tags: ['subsea pipeline', 'flowline', 'export pipeline', 'trunkline', 'pipeline end manifold', 'plem'],
    functions: [
      { description: 'Transport hydrocarbons between subsea facilities', type: 'Primary', performanceStandard: 'Design flow rate at specified pressure, zero leakage' },
    ],
    failuremodes: [
      { description: 'Internal corrosion — wall loss', mechanism: 'CO2/H2S corrosion, erosion', cause: 'Corrosive fluid, sand production, insufficient inhibition', severity: 8, detectionMethod: 'Intelligent pigging, corrosion coupon' },
      { description: 'External corrosion — CP failure', mechanism: 'Coating damage + inadequate CP', cause: 'Mechanical damage, anode depletion', severity: 7, detectionMethod: 'CP survey, ROV inspection' },
      { description: 'Hydrate/wax blockage', mechanism: 'Flow assurance failure', cause: 'Temperature drop, inhibitor failure, shutdown', severity: 6, detectionMethod: 'Pressure differential, flow monitoring' },
    ],
    scheduledtasks: [
      { description: 'Intelligent pig run', interval: 43800, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'CP survey', interval: 8760, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'ROV inspection — freespans, seabed', interval: 8760, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Pigging for wax/deposit removal', interval: 4380, intervalUnit: 'hours', strategy: 'Fixed Time' },
    ],
    consequences: [
      { description: 'Subsea hydrocarbon release', category: 'Environmental', severity: 10, likelihood: 1 },
      { description: 'Production shutdown for pipeline repair', category: 'Operational', severity: 9, likelihood: 2 },
    ]
  },

  // =========================================================================
  // SFG20 — BUILDING SERVICES (Additional)
  // =========================================================================
  'Domestic Water System': {
    category: 'Building Services', standard: 'SFG20 WS-01/WS-02',
    tags: ['domestic water', 'cold water', 'hot water', 'calorifier', 'water heater', 'storage vessel', 'expansion vessel', 'tmv', 'thermostatic mixing valve'],
    functions: [
      { description: 'Supply potable hot and cold water to all outlets', type: 'Primary', performanceStandard: 'Hot water 50-60°C at outlet, cold water < 20°C within 2 minutes' },
      { description: 'Prevent Legionella proliferation', type: 'Safety', performanceStandard: 'Storage > 60°C, distribution > 55°C, cold < 20°C' },
    ],
    failuremodes: [
      { description: 'Calorifier failure — no hot water', mechanism: 'Coil perforation, thermostat failure', cause: 'Corrosion, scale, age', severity: 5, detectionMethod: 'Temperature monitoring' },
      { description: 'TMV failure — scalding risk', mechanism: 'Wax element failure', cause: 'Scale, debris, age', severity: 8, detectionMethod: 'Temperature check at outlet' },
      { description: 'Legionella risk — low temperature', mechanism: 'Dead legs, low flow, low temperature', cause: 'Poor design, valve failure, thermostat fault', severity: 9, detectionMethod: 'Temperature monitoring, water sampling' },
    ],
    scheduledtasks: [
      { description: 'Temperature monitoring — sentinel outlets', interval: 720, intervalUnit: 'hours', strategy: 'Condition Based', sfg20Ref: 'SFG20 WS-01' },
      { description: 'TMV fail-safe check', interval: 4380, intervalUnit: 'hours', strategy: 'Fixed Time', sfg20Ref: 'SFG20 WS-02' },
      { description: 'Calorifier inspection and descale', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time', sfg20Ref: 'SFG20 WS-01' },
      { description: 'Legionella risk assessment review', interval: 17520, intervalUnit: 'hours', strategy: 'Fixed Time' },
    ],
    consequences: [
      { description: 'Legionella outbreak', category: 'Safety', severity: 10, likelihood: 2 },
      { description: 'Scalding from TMV failure', category: 'Safety', severity: 8, likelihood: 2 },
    ]
  },

  'Building Management System': {
    category: 'Building Services', standard: 'SFG20 BM-01',
    tags: ['bms', 'building management', 'bacs', 'controls', 'building automation', 'trend', 'honeywell', 'schneider'],
    functions: [
      { description: 'Monitor and control HVAC, lighting, and building services', type: 'Primary', performanceStandard: 'System availability > 99%, accurate setpoint control ±1°C' },
    ],
    failuremodes: [
      { description: 'Controller failure — loss of automatic control', mechanism: 'Electronics failure', cause: 'Power surge, component age', severity: 6, detectionMethod: 'BMS alarm, manual override required' },
      { description: 'Sensor failure — incorrect readings', mechanism: 'Sensor drift/failure', cause: 'Age, environmental conditions', severity: 5, detectionMethod: 'Comparison with space conditions' },
    ],
    scheduledtasks: [
      { description: 'Sensor calibration verification', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time', sfg20Ref: 'SFG20 BM-01' },
      { description: 'Software backup', interval: 4380, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Time schedule and setpoint review', interval: 4380, intervalUnit: 'hours', strategy: 'Fixed Time' },
    ],
    consequences: [
      { description: 'Comfort complaints — uncontrolled environment', category: 'Operational', severity: 4, likelihood: 4 },
      { description: 'Energy waste — heating and cooling simultaneously', category: 'Operational', severity: 5, likelihood: 3 },
    ]
  },

  'Access Control/Security System': {
    category: 'Building Services', standard: 'SFG20 SC-01',
    tags: ['access control', 'security', 'cctv', 'intruder alarm', 'door entry', 'barrier', 'turnstile'],
    functions: [
      { description: 'Control and record access to secured areas', type: 'Primary', performanceStandard: 'Card/biometric read within 2 seconds, audit trail maintained' },
    ],
    failuremodes: [
      { description: 'Reader failure — unable to authenticate', mechanism: 'Reader electronics failure', cause: 'Power failure, vandalism, weather', severity: 6, detectionMethod: 'Access attempt failure, alarm' },
      { description: 'Lock fail — door unsecured', mechanism: 'Maglock/strike failure', cause: 'Power failure (without battery backup), mechanical wear', severity: 7, detectionMethod: 'Door contact monitoring' },
    ],
    scheduledtasks: [
      { description: 'System function test — readers and locks', interval: 4380, intervalUnit: 'hours', strategy: 'Fixed Time', sfg20Ref: 'SFG20 SC-01' },
      { description: 'Battery backup test', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'CCTV lens clean and focus check', interval: 4380, intervalUnit: 'hours', strategy: 'Fixed Time' },
    ],
    consequences: [
      { description: 'Unauthorised access to secured area', category: 'Safety', severity: 7, likelihood: 3 },
    ]
  },

  'Drainage System': {
    category: 'Building Services', standard: 'SFG20 DR-01',
    tags: ['drainage', 'foul drainage', 'surface water', 'sewer', 'rainwater', 'gutter', 'downpipe', 'grease trap', 'pump station'],
    functions: [
      { description: 'Remove foul and surface water from building/site', type: 'Primary', performanceStandard: 'Free-flowing drainage without surcharge at design rainfall' },
    ],
    failuremodes: [
      { description: 'Blockage — drain surcharge', mechanism: 'Debris/grease accumulation', cause: 'Fat/oil/grease, foreign objects, root ingress', severity: 6, detectionMethod: 'Slow drainage, CCTV survey' },
      { description: 'Pipe fracture/collapse', mechanism: 'Structural failure', cause: 'Ground movement, root damage, age', severity: 7, detectionMethod: 'CCTV survey, ground subsidence' },
    ],
    scheduledtasks: [
      { description: 'Gutter and downpipe clean', interval: 4380, intervalUnit: 'hours', strategy: 'Fixed Time', sfg20Ref: 'SFG20 DR-01' },
      { description: 'Grease trap clean', interval: 2160, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'CCTV drain survey', interval: 43800, intervalUnit: 'hours', strategy: 'Condition Based' },
    ],
    consequences: [
      { description: 'Flooding and water damage', category: 'Operational', severity: 7, likelihood: 3 },
      { description: 'Health hazard from foul water', category: 'Safety', severity: 6, likelihood: 2 },
    ]
  },

  'Swimming Pool Plant': {
    category: 'Building Services', standard: 'SFG20 SP-01',
    tags: ['swimming pool', 'pool plant', 'pool pump', 'pool filter', 'chlorination', 'dosing', 'pool heater'],
    functions: [
      { description: 'Maintain pool water quality and temperature', type: 'Primary', performanceStandard: 'Free chlorine 1-3 mg/l, pH 7.2-7.4, temperature 26-28°C' },
    ],
    failuremodes: [
      { description: 'Chlorination failure — water quality', mechanism: 'Dosing pump/probe failure', cause: 'Probe fouling, pump malfunction', severity: 7, detectionMethod: 'Manual water test, BMS alarm' },
      { description: 'Filtration failure — turbid water', mechanism: 'Filter media degraded, pump failure', cause: 'Age, backwash failure', severity: 5, detectionMethod: 'Turbidity monitoring' },
    ],
    scheduledtasks: [
      { description: 'Water quality test — chlorine, pH, temperature', interval: 4, intervalUnit: 'hours', strategy: 'Condition Based', sfg20Ref: 'SFG20 SP-01' },
      { description: 'Filter backwash', interval: 168, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Full plant service', interval: 4380, intervalUnit: 'hours', strategy: 'Fixed Time', sfg20Ref: 'SFG20 SP-01' },
    ],
    consequences: [
      { description: 'Pool closure — public health risk', category: 'Safety', severity: 7, likelihood: 3 },
    ]
  },

  'Standby Generator': {
    category: 'Building Services', standard: 'SFG20 EG-01',
    tags: ['standby generator', 'backup generator', 'emergency power', 'genset building', 'ats', 'auto transfer switch'],
    functions: [
      { description: 'Provide emergency power on mains failure', type: 'Safety', performanceStandard: 'Start and accept load within 15 seconds of mains loss' },
    ],
    failuremodes: [
      { description: 'Failure to start on demand', mechanism: 'Starting system/fuel supply fault', cause: 'Battery flat, fuel contamination, control fault', severity: 9, detectionMethod: 'Weekly start test' },
      { description: 'ATS failure — no changeover', mechanism: 'Contactor/relay failure', cause: 'Component age, corrosion, control fault', severity: 8, detectionMethod: 'ATS function test' },
    ],
    scheduledtasks: [
      { description: 'Weekly no-load start test', interval: 168, intervalUnit: 'hours', strategy: 'Fixed Time', sfg20Ref: 'SFG20 EG-01' },
      { description: 'Monthly load test', interval: 720, intervalUnit: 'hours', strategy: 'Fixed Time', sfg20Ref: 'SFG20 EG-01' },
      { description: 'Annual full load transfer test', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Oil and filter change', interval: 500, intervalUnit: 'hours', strategy: 'Fixed Time' },
    ],
    consequences: [
      { description: 'No emergency power — life safety systems fail', category: 'Safety', severity: 9, likelihood: 2 },
    ]
  },

  'Solar PV System': {
    category: 'Electrical', standard: 'IEC 62446 / SFG20 PV-01',
    tags: ['solar', 'photovoltaic', 'pv', 'solar panel', 'solar inverter', 'string inverter'],
    functions: [
      { description: 'Convert solar irradiance to electrical power', type: 'Primary', performanceStandard: 'Performance ratio > 80% under STC conditions' },
    ],
    failuremodes: [
      { description: 'Inverter failure — no AC output', mechanism: 'Power electronics failure', cause: 'Thermal stress, capacitor degradation, grid fault', severity: 6, detectionMethod: 'Monitoring system, zero output alarm' },
      { description: 'Module degradation — reduced output', mechanism: 'PID, hot spots, delamination', cause: 'Age, manufacturing defect, soiling', severity: 4, detectionMethod: 'Performance monitoring, IR survey' },
      { description: 'DC arc fault — fire risk', mechanism: 'Loose connector, cable damage', cause: 'Poor installation, UV degradation, rodent damage', severity: 8, detectionMethod: 'Arc fault detection, IR survey' },
    ],
    scheduledtasks: [
      { description: 'Performance monitoring — PR trending', interval: 720, intervalUnit: 'hours', strategy: 'Condition Based', sfg20Ref: 'SFG20 PV-01' },
      { description: 'Visual inspection and panel cleaning', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Thermographic survey', interval: 17520, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Inverter service — fan/capacitor check', interval: 17520, intervalUnit: 'hours', strategy: 'Fixed Time' },
    ],
    consequences: [
      { description: 'Lost energy generation revenue', category: 'Operational', severity: 5, likelihood: 4 },
      { description: 'Roof fire from DC arc fault', category: 'Safety', severity: 8, likelihood: 1 },
    ]
  },

  // =========================================================================
  // MECHANICAL / MATERIAL HANDLING (Additional)
  // =========================================================================
  'Winch': {
    category: 'Mechanical', standard: 'ISO 14224',
    tags: ['winch', 'capstan', 'tugger winch', 'mooring winch', 'anchor winch'],
    functions: [
      { description: 'Apply controlled pulling force via wire/rope', type: 'Primary', performanceStandard: 'Rated SWL, line speed per specification' },
    ],
    failuremodes: [
      { description: 'Brake failure — uncontrolled pay-out', mechanism: 'Brake pad wear, hydraulic leak', cause: 'Overload, lack of maintenance', severity: 9, detectionMethod: 'Brake test, visual inspection' },
      { description: 'Wire rope degradation', mechanism: 'Fatigue, corrosion, abrasion', cause: 'Sheave wear, overload, environment', severity: 8, detectionMethod: 'MRT inspection, visual broken wire count' },
    ],
    scheduledtasks: [
      { description: 'Brake holding test', interval: 4380, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Wire rope inspection', interval: 720, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Gearbox oil change', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' },
    ],
    consequences: [
      { description: 'Dropped load — personnel injury', category: 'Safety', severity: 9, likelihood: 2 },
    ]
  },

  'Pedestal Crane (Offshore)': {
    category: 'Mechanical', standard: 'LOLER / NORSOK R-003',
    tags: ['pedestal crane', 'offshore crane', 'deck crane', 'knuckle boom', 'lattice boom'],
    functions: [
      { description: 'Lift and position loads safely within rated capacity', type: 'Primary', performanceStandard: 'SWL per load chart, within rated radius/height' },
    ],
    failuremodes: [
      { description: 'Slew bearing degradation', mechanism: 'Roller/raceway wear', cause: 'Overload, corrosion, age', severity: 8, detectionMethod: 'Bolt tension monitoring, backlash measurement' },
      { description: 'Boom structural crack', mechanism: 'Fatigue', cause: 'Cyclic loading, weld defect', severity: 9, detectionMethod: 'MPI at weld details' },
      { description: 'Safety system failure — LMI/SLI', mechanism: 'Electronics/sensor failure', cause: 'Calibration drift, component failure', severity: 8, detectionMethod: 'Function test, calibration check' },
    ],
    scheduledtasks: [
      { description: 'Daily pre-use checks', interval: 24, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'LMI/SLI function test', interval: 720, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: '6-monthly thorough examination (LOLER)', interval: 4380, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Annual structural inspection — NDT', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Slew bearing bolt tension check', interval: 17520, intervalUnit: 'hours', strategy: 'Condition Based' },
    ],
    consequences: [
      { description: 'Dropped load — fatality risk', category: 'Safety', severity: 10, likelihood: 1 },
      { description: 'Crane out of service — logistics impact', category: 'Operational', severity: 7, likelihood: 3 },
    ]
  },

  'Compactor/Baler': {
    category: 'Mechanical', standard: 'BS EN 16486',
    tags: ['compactor', 'baler', 'waste compactor', 'refuse compactor'],
    functions: [
      { description: 'Compact waste material to reduce volume for disposal', type: 'Primary', performanceStandard: 'Volume reduction ratio per specification' },
    ],
    failuremodes: [
      { description: 'Hydraulic cylinder failure', mechanism: 'Seal leak, rod scoring', cause: 'Contamination, overload, age', severity: 5, detectionMethod: 'Cycle time increase, oil leak' },
      { description: 'Safety interlock failure', mechanism: 'Switch/relay failure', cause: 'Contamination, age', severity: 8, detectionMethod: 'Function test' },
    ],
    scheduledtasks: [
      { description: 'Safety interlock test', interval: 720, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Hydraulic system inspection', interval: 4380, intervalUnit: 'hours', strategy: 'Fixed Time' },
    ],
    consequences: [
      { description: 'Personnel injury — entrapment', category: 'Safety', severity: 9, likelihood: 1 },
    ]
  },

  'Dock Leveller': {
    category: 'Mechanical', standard: 'BS EN 1398',
    tags: ['dock leveller', 'loading dock', 'dock shelter', 'vehicle restraint'],
    functions: [
      { description: 'Bridge height difference between loading dock and vehicle', type: 'Primary', performanceStandard: 'Rated load capacity, smooth transition angle' },
    ],
    failuremodes: [
      { description: 'Hydraulic failure — won\'t raise/lower', mechanism: 'Pump/cylinder failure', cause: 'Oil leak, motor failure', severity: 5, detectionMethod: 'Function test' },
      { description: 'Lip hinge failure — collapse', mechanism: 'Fatigue fracture', cause: 'Overload, impact damage', severity: 8, detectionMethod: 'Visual inspection, load test' },
    ],
    scheduledtasks: [
      { description: 'Function test and visual inspection', interval: 4380, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Annual thorough examination', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' },
    ],
    consequences: [
      { description: 'Personnel fall — injury', category: 'Safety', severity: 7, likelihood: 2 },
    ]
  },

  // =========================================================================
  // PROCESS & UTILITY SYSTEMS
  // =========================================================================
  'Pressure Reducing Station': {
    category: 'Static', standard: 'IGE/UP/2',
    tags: ['pressure reducing station', 'prv station', 'gas governor', 'pressure regulator', 'gas meter'],
    functions: [
      { description: 'Reduce gas supply pressure to downstream requirement', type: 'Primary', performanceStandard: 'Outlet pressure within ±5% of setpoint, slam-shut functional' },
    ],
    failuremodes: [
      { description: 'Regulator failure — overpressure downstream', mechanism: 'Diaphragm rupture, seat erosion', cause: 'Age, contamination, fatigue', severity: 8, detectionMethod: 'Downstream pressure monitoring' },
      { description: 'Slam-shut fail to operate', mechanism: 'Mechanism seizure', cause: 'Corrosion, lack of testing', severity: 9, detectionMethod: 'Function test' },
    ],
    scheduledtasks: [
      { description: 'Slam-shut function test', interval: 4380, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Regulator setpoint check', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Full overhaul and recertification', interval: 43800, intervalUnit: 'hours', strategy: 'Fixed Time' },
    ],
    consequences: [
      { description: 'Downstream overpressure — equipment damage/gas release', category: 'Safety', severity: 9, likelihood: 2 },
    ]
  },

  'Cooling Tower': {
    category: 'Static', standard: 'CTI Standards',
    tags: ['cooling tower', 'evaporative cooler', 'wet cooling', 'dry cooler', 'cooling water'],
    functions: [
      { description: 'Reject heat from cooling water circuit to atmosphere', type: 'Primary', performanceStandard: 'Approach temperature within 3°C of design wet bulb' },
    ],
    failuremodes: [
      { description: 'Fill media fouling/collapse', mechanism: 'Biological growth, scale, structural failure', cause: 'Poor water treatment, UV degradation', severity: 6, detectionMethod: 'Approach temperature increase, visual' },
      { description: 'Fan failure — reduced airflow', mechanism: 'Bearing failure, blade damage', cause: 'Imbalance, vibration, age', severity: 6, detectionMethod: 'Vibration monitoring, outlet temperature' },
      { description: 'Legionella risk', mechanism: 'Biofilm growth in warm water', cause: 'Poor water treatment, drift eliminator failure', severity: 9, detectionMethod: 'Water sampling, temperature monitoring' },
    ],
    scheduledtasks: [
      { description: 'Water treatment monitoring', interval: 168, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Drift eliminator inspection', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Fan vibration survey', interval: 2160, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Legionella risk assessment', interval: 17520, intervalUnit: 'hours', strategy: 'Fixed Time' },
    ],
    consequences: [
      { description: 'Reduced cooling capacity — process impact', category: 'Operational', severity: 6, likelihood: 4 },
      { description: 'Legionella outbreak', category: 'Safety', severity: 10, likelihood: 1 },
    ]
  },

  'Water Treatment Plant': {
    category: 'Static', standard: 'ISO 14224',
    tags: ['water treatment', 'ro', 'reverse osmosis', 'deioniser', 'softener', 'water plant', 'demineraliser', 'edi'],
    functions: [
      { description: 'Produce treated water to required quality specification', type: 'Primary', performanceStandard: 'Conductivity/TDS/hardness within specification' },
    ],
    failuremodes: [
      { description: 'Membrane fouling — reduced throughput', mechanism: 'Organic/inorganic fouling', cause: 'Poor pretreatment, scaling', severity: 5, detectionMethod: 'Differential pressure, flow rate' },
      { description: 'Resin exhaustion — breakthrough', mechanism: 'Ion exchange capacity depleted', cause: 'High demand, regeneration failure', severity: 5, detectionMethod: 'Conductivity monitoring' },
    ],
    scheduledtasks: [
      { description: 'Water quality monitoring', interval: 8, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Membrane clean-in-place (CIP)', interval: 4380, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Resin regeneration', interval: 168, intervalUnit: 'hours', strategy: 'Fixed Time' },
    ],
    consequences: [
      { description: 'Off-spec water — boiler/process damage', category: 'Operational', severity: 7, likelihood: 3 },
    ]
  },

  'Compressed Air System': {
    category: 'Static', standard: 'ISO 8573',
    tags: ['compressed air', 'air receiver', 'air dryer', 'instrument air', 'plant air', 'air system'],
    functions: [
      { description: 'Supply clean dry compressed air at required pressure', type: 'Primary', performanceStandard: 'ISO 8573 quality class, pressure ±0.5 bar' },
    ],
    failuremodes: [
      { description: 'Air dryer failure — wet air', mechanism: 'Desiccant exhaustion, valve failure', cause: 'Age, excessive load, control fault', severity: 6, detectionMethod: 'Dew point monitoring' },
      { description: 'System pressure drop — undersupply', mechanism: 'Leak accumulation', cause: 'Aging fittings, vibration, poor joints', severity: 5, detectionMethod: 'Pressure monitoring, leak survey' },
    ],
    scheduledtasks: [
      { description: 'Dew point monitoring', interval: 720, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Ultrasonic leak survey', interval: 8760, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Air receiver statutory inspection', interval: 43800, intervalUnit: 'hours', strategy: 'Fixed Time' },
    ],
    consequences: [
      { description: 'Instrument air failure — plant trip', category: 'Operational', severity: 8, likelihood: 3 },
    ]
  },

  'Nitrogen Generation System': {
    category: 'Static', standard: 'Best Practice',
    tags: ['nitrogen', 'nitrogen generator', 'psa nitrogen', 'membrane nitrogen', 'n2', 'inert gas'],
    functions: [
      { description: 'Generate nitrogen at required purity and pressure', type: 'Primary', performanceStandard: 'Purity > 99.5% (or as specified), rated flow' },
    ],
    failuremodes: [
      { description: 'Purity drop — oxygen breakthrough', mechanism: 'Membrane/bed degradation', cause: 'Contamination, age, valve failure', severity: 7, detectionMethod: 'Oxygen analyser' },
      { description: 'Compressor failure — no feed air', mechanism: 'Compressor mechanical failure', cause: 'See Compressor class', severity: 7, detectionMethod: 'Pressure/flow monitoring' },
    ],
    scheduledtasks: [
      { description: 'Purity (O2) monitoring', interval: 168, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Membrane/bed replacement', interval: 43800, intervalUnit: 'hours', strategy: 'Fixed Time' },
    ],
    consequences: [
      { description: 'Loss of inerting — explosive atmosphere risk', category: 'Safety', severity: 9, likelihood: 2 },
    ]
  },

  'Waste Water Treatment': {
    category: 'Static', standard: 'ISO 14224',
    tags: ['waste water', 'effluent', 'sewage treatment', 'aeration', 'clarifier', 'oily water separator', 'ows'],
    functions: [
      { description: 'Treat effluent to discharge consent standards', type: 'Primary', performanceStandard: 'BOD, COD, TSS, oil-in-water within consent limits' },
    ],
    failuremodes: [
      { description: 'Biological treatment upset — poor effluent quality', mechanism: 'Biomass die-off', cause: 'Toxic slug, temperature shock, nutrient imbalance', severity: 7, detectionMethod: 'Effluent quality monitoring' },
      { description: 'Aeration system failure', mechanism: 'Blower failure, diffuser blockage', cause: 'Mechanical failure, fouling', severity: 6, detectionMethod: 'Dissolved oxygen monitoring' },
    ],
    scheduledtasks: [
      { description: 'Effluent quality monitoring', interval: 24, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Sludge level check — settle test', interval: 168, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Diffuser cleaning/replacement', interval: 17520, intervalUnit: 'hours', strategy: 'Fixed Time' },
    ],
    consequences: [
      { description: 'Consent breach — prosecution', category: 'Environmental', severity: 8, likelihood: 3 },
    ]
  },

  // GENERIC — catches anything not specifically matched
  // =========================================================================
  '_Generic': {
    category: 'Generic', standard: 'Best Practice',
    tags: [],
    functions: [
      { description: 'Perform intended function within operating envelope', type: 'Primary', performanceStandard: 'As per design specification' },
      { description: 'Contain process/product without release', type: 'Primary', performanceStandard: 'Zero external leakage' },
      { description: 'Operate without hazard to personnel or environment', type: 'Safety', performanceStandard: 'All safety devices functional' }
    ],
    failuremodes: [
      { description: 'Loss of primary function', mechanism: 'Component degradation or failure', cause: 'Wear, age, environmental stress, overload', severity: 6, detectionMethod: 'Performance monitoring, operator observation' },
      { description: 'External leakage', mechanism: 'Seal/gasket degradation', cause: 'Age, thermal cycling, chemical attack', severity: 5, detectionMethod: 'Visual inspection, leak detection' },
      { description: 'Excessive vibration/noise', mechanism: 'Bearing degradation, imbalance, looseness', cause: 'Wear, misalignment, foundation settling', severity: 5, detectionMethod: 'Vibration monitoring, operator observation' },
      { description: 'Structural degradation', mechanism: 'Corrosion, fatigue, erosion', cause: 'Environmental exposure, cyclic loading', severity: 7, detectionMethod: 'Visual inspection, thickness measurement' },
      { description: 'Control/instrumentation fault', mechanism: 'Sensor drift, controller failure', cause: 'Age, environmental stress, power disturbance', severity: 5, detectionMethod: 'Calibration check, diagnostics' }
    ],
    scheduledtasks: [
      { description: 'Visual inspection — condition assessment', interval: 720, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Vibration monitoring (if rotating)', interval: 720, intervalUnit: 'hours', strategy: 'Condition Based' },
      { description: 'Lubrication per OEM schedule', interval: 4380, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Functional test — safety and protective devices', interval: 8760, intervalUnit: 'hours', strategy: 'Fixed Time' },
      { description: 'Comprehensive condition survey', interval: 17520, intervalUnit: 'hours', strategy: 'Fixed Time' }
    ],
    consequences: [
      { description: 'Loss of function — production impact', category: 'Operational', severity: 5, likelihood: 3 },
      { description: 'Safety system unavailable', category: 'Safety', severity: 7, likelihood: 2 }
    ]
  }
};

// ============================================================================
// EQUIPMENT TYPE ALIASES — fuzzy matching from common names to taxonomy keys
// ============================================================================
const TYPE_ALIASES = {
  // Pumps
  'pump': 'Centrifugal Pump', 'centrifugal pump': 'Centrifugal Pump', 'booster pump': 'Centrifugal Pump', 'process pump': 'Centrifugal Pump', 'feed pump': 'Centrifugal Pump', 'cooling water pump': 'Centrifugal Pump', 'charge pump': 'Centrifugal Pump', 'reflux pump': 'Centrifugal Pump', 'transfer pump': 'Centrifugal Pump', 'sump pump': 'Centrifugal Pump',
  'reciprocating pump': 'Reciprocating Pump', 'positive displacement pump': 'Reciprocating Pump', 'plunger pump': 'Reciprocating Pump', 'diaphragm pump': 'Reciprocating Pump', 'metering pump': 'Reciprocating Pump', 'dosing pump': 'Reciprocating Pump', 'injection pump': 'Reciprocating Pump', 'triplex pump': 'Reciprocating Pump',
  // Motors & drives
  'motor': 'Electric Motor', 'electric motor': 'Electric Motor', 'induction motor': 'Electric Motor', 'synchronous motor': 'Electric Motor', 'dc motor': 'Electric Motor',
  'vsd': 'Variable Speed Drive', 'vfd': 'Variable Speed Drive', 'variable speed drive': 'Variable Speed Drive', 'variable frequency drive': 'Variable Speed Drive', 'frequency converter': 'Variable Speed Drive', 'inverter drive': 'Variable Speed Drive', 'soft starter': 'Variable Speed Drive',
  // Compressors
  'compressor': 'Compressor', 'axial compressor': 'Compressor', 'air compressor': 'Compressor', 'gas compressor': 'Compressor', 'refrigeration compressor': 'Compressor',
  // Turbines
  'gas turbine': 'Gas Turbine', 'gt': 'Gas Turbine', 'aeroderivative': 'Gas Turbine', 'frame turbine': 'Gas Turbine', 'turbine generator': 'Gas Turbine',
  'steam turbine': 'Steam Turbine', 'st': 'Steam Turbine', 'condensing turbine': 'Steam Turbine', 'back-pressure turbine': 'Steam Turbine', 'extraction turbine': 'Steam Turbine', 'turbine drive': 'Steam Turbine',
  // Heat transfer
  'heat exchanger': 'Heat Exchanger', 'cooler': 'Heat Exchanger', 'condenser': 'Heat Exchanger', 'reboiler': 'Heat Exchanger', 'preheater': 'Heat Exchanger', 'economiser': 'Heat Exchanger', 'plate exchanger': 'Heat Exchanger', 'fin fan': 'Heat Exchanger', 'air cooled': 'Heat Exchanger', 'shell and tube': 'Heat Exchanger',
  'boiler': 'Boiler', 'steam boiler': 'Boiler', 'water tube boiler': 'Boiler', 'fire tube boiler': 'Boiler', 'hrsg': 'Boiler', 'waste heat boiler': 'Boiler',
  'furnace': 'Furnace/Fired Heater', 'fired heater': 'Furnace/Fired Heater', 'process heater': 'Furnace/Fired Heater', 'reformer': 'Furnace/Fired Heater', 'thermal oxidiser': 'Furnace/Fired Heater', 'incinerator': 'Furnace/Fired Heater',
  // Fans
  'fan': 'Fan/Blower', 'blower': 'Fan/Blower', 'id fan': 'Fan/Blower', 'fd fan': 'Fan/Blower', 'induced draft fan': 'Fan/Blower', 'forced draft fan': 'Fan/Blower', 'exhaust fan': 'Fan/Blower', 'ventilation fan': 'Fan/Blower', 'process fan': 'Fan/Blower',
  // Gearbox
  'gearbox': 'Gearbox', 'gear box': 'Gearbox', 'speed reducer': 'Gearbox', 'speed increaser': 'Gearbox', 'epicyclic gearbox': 'Gearbox', 'planetary gearbox': 'Gearbox',
  // Generator
  'generator': 'Generator', 'alternator': 'Generator', 'synchronous generator': 'Generator', 'genset': 'Generator', 'diesel generator': 'Generator', 'emergency generator': 'Generator',
  // Static
  'pressure vessel': 'Pressure Vessel', 'vessel': 'Pressure Vessel', 'separator': 'Pressure Vessel', 'drum': 'Pressure Vessel', 'knockout drum': 'Pressure Vessel', 'column': 'Pressure Vessel', 'tower': 'Pressure Vessel', 'reactor': 'Pressure Vessel', 'accumulator': 'Pressure Vessel',
  'tank': 'Storage Tank', 'storage tank': 'Storage Tank', 'atmospheric tank': 'Storage Tank', 'floating roof': 'Storage Tank', 'fixed roof': 'Storage Tank', 'bullet': 'Storage Tank', 'sphere': 'Storage Tank', 'crude tank': 'Storage Tank', 'water tank': 'Storage Tank',
  'piping': 'Piping System', 'pipe': 'Piping System', 'pipeline': 'Piping System', 'pipework': 'Piping System',
  'filter': 'Filtration Unit', 'filtration unit': 'Filtration Unit', 'strainer': 'Filtration Unit', 'cartridge filter': 'Filtration Unit', 'sand filter': 'Filtration Unit',
  // Control & Safety
  'control valve': 'Control Valve', 'valve': 'Control Valve', 'globe valve': 'Control Valve', 'butterfly valve': 'Control Valve', 'ball valve': 'Control Valve', 'regulating valve': 'Control Valve',
  'esdv': 'Emergency Shutdown Valve', 'emergency shutdown valve': 'Emergency Shutdown Valve', 'sdv': 'Emergency Shutdown Valve', 'blowdown valve': 'Emergency Shutdown Valve', 'shutdown valve': 'Emergency Shutdown Valve',
  'plc': 'Programmable Logic Controller', 'controller': 'Programmable Logic Controller', 'dcs': 'Programmable Logic Controller', 'scada': 'Programmable Logic Controller', 'rtu': 'Programmable Logic Controller',
  'sis': 'Safety Instrumented System', 'safety system': 'Safety Instrumented System', 'sif': 'Safety Instrumented System', 'esd system': 'Safety Instrumented System', 'fire and gas': 'Safety Instrumented System',
  'transmitter': 'Instrument Transmitter', 'pressure transmitter': 'Instrument Transmitter', 'temperature transmitter': 'Instrument Transmitter', 'flow transmitter': 'Instrument Transmitter', 'level transmitter': 'Instrument Transmitter', 'analyser': 'Instrument Transmitter', 'gas detector': 'Instrument Transmitter',
  // Electrical
  'transformer': 'Transformer', 'power transformer': 'Transformer', 'distribution transformer': 'Transformer', 'step-up transformer': 'Transformer',
  'switchgear': 'Switchgear', 'circuit breaker': 'Switchgear', 'mcc': 'Switchgear', 'motor control centre': 'Switchgear', 'ring main unit': 'Switchgear', 'breaker': 'Switchgear',
  'ups': 'UPS/Battery System', 'battery': 'UPS/Battery System', 'battery system': 'UPS/Battery System', 'dc system': 'UPS/Battery System', 'charger': 'UPS/Battery System',
  // Mechanical
  'crane': 'Crane', 'overhead crane': 'Crane', 'gantry crane': 'Crane', 'mobile crane': 'Crane', 'hoist': 'Crane', 'davit': 'Crane',
  'conveyor': 'Conveyor', 'belt conveyor': 'Conveyor', 'screw conveyor': 'Conveyor', 'chain conveyor': 'Conveyor', 'bucket elevator': 'Conveyor',
  // Building Services (SFG20)
  'hvac': 'HVAC System', 'air conditioning': 'HVAC System', 'ahu': 'HVAC System', 'air handling unit': 'HVAC System', 'chiller': 'HVAC System', 'heat pump': 'HVAC System', 'fan coil': 'HVAC System', 'vrf': 'HVAC System', 'package unit': 'HVAC System', 'split system': 'HVAC System',
  'lift': 'Lift/Elevator', 'elevator': 'Lift/Elevator', 'escalator': 'Lift/Elevator', 'travelator': 'Lift/Elevator', 'platform lift': 'Lift/Elevator',
  'fire alarm': 'Fire Detection/Suppression', 'fire detection': 'Fire Detection/Suppression', 'sprinkler': 'Fire Detection/Suppression', 'fire suppression': 'Fire Detection/Suppression', 'deluge': 'Fire Detection/Suppression', 'smoke detector': 'Fire Detection/Suppression', 'fire hydrant': 'Fire Detection/Suppression', 'dry riser': 'Fire Detection/Suppression',
  // Additional equipment types
  'centrifugal compressor': 'Centrifugal Compressor',
  'turbo compressor': 'Centrifugal Compressor',
  'barrel compressor': 'Centrifugal Compressor',
  'screw compressor': 'Screw Compressor',
  'rotary screw': 'Screw Compressor',
  'oil-free compressor': 'Screw Compressor',
  'reciprocating compressor': 'Reciprocating Compressor',
  'recip compressor': 'Reciprocating Compressor',
  'piston compressor': 'Reciprocating Compressor',
  'diesel engine': 'Diesel Engine',
  'fire pump engine': 'Diesel Engine',
  'prime mover': 'Diesel Engine',
  'engine': 'Diesel Engine',
  'hpu': 'Hydraulic Power Unit',
  'hydraulic power unit': 'Hydraulic Power Unit',
  'hydraulic pump': 'Hydraulic Power Unit',
  'hydraulic system': 'Hydraulic Power Unit',
  'agitator': 'Agitator/Mixer',
  'mixer': 'Agitator/Mixer',
  'stirrer': 'Agitator/Mixer',
  'centrifuge': 'Centrifuge',
  'decanter': 'Centrifuge',
  'disc separator': 'Centrifuge',
  'wind turbine': 'Wind Turbine',
  'wtg': 'Wind Turbine',
  'wind generator': 'Wind Turbine',
  'distillation column': 'Distillation Column',
  'fractionator': 'Distillation Column',
  'absorber': 'Distillation Column',
  'scrubber': 'Distillation Column',
  'packed column': 'Distillation Column',
  'tray column': 'Distillation Column',
  'flare': 'Flare System',
  'flare stack': 'Flare System',
  'flare tip': 'Flare System',
  'loading arm': 'Loading Arm',
  'marine loading arm': 'Loading Arm',
  'chicsan': 'Loading Arm',
  'gasket': 'Gasket/Flange Assembly',
  'flange': 'Gasket/Flange Assembly',
  'bolted joint': 'Gasket/Flange Assembly',
  'expansion joint': 'Expansion Joint',
  'bellows': 'Expansion Joint',
  'expansion bellows': 'Expansion Joint',
  'power cable': 'Power Cable',
  'mv cable': 'Power Cable',
  'hv cable': 'Power Cable',
  'capacitor bank': 'Capacitor Bank',
  'pfc': 'Capacitor Bank',
  'power factor correction': 'Capacitor Bank',
  'earthing': 'Earthing/Grounding System',
  'grounding': 'Earthing/Grounding System',
  'earth electrode': 'Earthing/Grounding System',
  'emergency lighting': 'Emergency Lighting',
  'emergency luminaire': 'Emergency Lighting',
  'exit sign': 'Emergency Lighting',
  'lightning protection': 'Lightning Protection System',
  'lps': 'Lightning Protection System',
  'psv': 'Pressure Safety Valve',
  'safety valve': 'Pressure Safety Valve',
  'relief valve': 'Pressure Safety Valve',
  'prv': 'Pressure Safety Valve',
  'pressure relief valve': 'Pressure Safety Valve',
  'flow meter': 'Flow Meter',
  'orifice plate': 'Flow Meter',
  'coriolis meter': 'Flow Meter',
  'ultrasonic meter': 'Flow Meter',
  'mag meter': 'Flow Meter',
  'turbine meter': 'Flow Meter',
  'level gauge': 'Level Instrument',
  'level switch': 'Level Instrument',
  'radar level': 'Level Instrument',
  'guided wave': 'Level Instrument',
  'displacer': 'Level Instrument',
  'thermocouple': 'Temperature Instrument',
  'rtd': 'Temperature Instrument',
  'temperature element': 'Temperature Instrument',
  'pyrometer': 'Temperature Instrument',
  'pressure gauge': 'Pressure Instrument',
  'pressure switch': 'Pressure Instrument',
  'manometer': 'Pressure Instrument',
  'bourdon tube': 'Pressure Instrument',
  'gas detection': 'Gas Detection System',
  'lel detector': 'Gas Detection System',
  'h2s detector': 'Gas Detection System',
  'open path detector': 'Gas Detection System',
  'fire damper': 'Fire Damper',
  'smoke damper': 'Fire Damper',
  'electric actuator': 'Actuator (Electric)',
  'motorised actuator': 'Actuator (Electric)',
  'moa': 'Actuator (Electric)',
  'pneumatic actuator': 'Actuator (Pneumatic)',
  'diaphragm actuator': 'Actuator (Pneumatic)',
  'scada system': 'SCADA/DCS System',
  'dcs system': 'SCADA/DCS System',
  'distributed control': 'SCADA/DCS System',
  'hmi': 'SCADA/DCS System',
  'christmas tree': 'Subsea Christmas Tree',
  'xmas tree': 'Subsea Christmas Tree',
  'subsea tree': 'Subsea Christmas Tree',
  'umbilical': 'Subsea Umbilical',
  'subsea umbilical': 'Subsea Umbilical',
  'riser': 'Riser',
  'production riser': 'Riser',
  'flexible riser': 'Riser',
  'scr': 'Riser',
  'subsea pipeline': 'Subsea Pipeline',
  'flowline': 'Subsea Pipeline',
  'trunkline': 'Subsea Pipeline',
  'domestic water': 'Domestic Water System',
  'calorifier': 'Domestic Water System',
  'water heater': 'Domestic Water System',
  'tmv': 'Domestic Water System',
  'bms': 'Building Management System',
  'building automation': 'Building Management System',
  'bacs': 'Building Management System',
  'access control': 'Access Control/Security System',
  'cctv': 'Access Control/Security System',
  'intruder alarm': 'Access Control/Security System',
  'drainage': 'Drainage System',
  'grease trap': 'Drainage System',
  'swimming pool': 'Swimming Pool Plant',
  'pool plant': 'Swimming Pool Plant',
  'standby generator': 'Standby Generator',
  'backup generator': 'Standby Generator',
  'emergency power': 'Standby Generator',
  'ats': 'Standby Generator',
  'solar pv': 'Solar PV System',
  'solar panel': 'Solar PV System',
  'photovoltaic': 'Solar PV System',
  'solar inverter': 'Solar PV System',
  'winch': 'Winch',
  'tugger winch': 'Winch',
  'mooring winch': 'Winch',
  'pedestal crane': 'Pedestal Crane (Offshore)',
  'offshore crane': 'Pedestal Crane (Offshore)',
  'deck crane': 'Pedestal Crane (Offshore)',
  'compactor': 'Compactor/Baler',
  'baler': 'Compactor/Baler',
  'waste compactor': 'Compactor/Baler',
  'dock leveller': 'Dock Leveller',
  'loading dock': 'Dock Leveller',
  'pressure reducing station': 'Pressure Reducing Station',
  'gas governor': 'Pressure Reducing Station',
  'prv station': 'Pressure Reducing Station',
  'cooling tower': 'Cooling Tower',
  'evaporative cooler': 'Cooling Tower',
  'water treatment': 'Water Treatment Plant',
  'reverse osmosis': 'Water Treatment Plant',
  'ro plant': 'Water Treatment Plant',
  'softener': 'Water Treatment Plant',
  'demineraliser': 'Water Treatment Plant',
  'compressed air': 'Compressed Air System',
  'air receiver': 'Compressed Air System',
  'air dryer': 'Compressed Air System',
  'instrument air': 'Compressed Air System',
  'nitrogen generator': 'Nitrogen Generation System',
  'psa nitrogen': 'Nitrogen Generation System',
  'n2 generator': 'Nitrogen Generation System',
  'waste water': 'Waste Water Treatment',
  'effluent treatment': 'Waste Water Treatment',
  'oily water separator': 'Waste Water Treatment',
  'ows': 'Waste Water Treatment',

};

// ============================================================================
// AI INFERENCE ENGINE — intelligent suggestion generation
// ============================================================================

/**
 * resolveEquipmentType — multi-strategy matching:
 *  1. Direct alias match
 *  2. Partial keyword match against aliases
 *  3. Tag-based matching against taxonomy entries
 *  4. Fallback to _Generic with context-aware suggestions
 */
function resolveEquipmentType(rawType) {
  const type = (rawType || '').toLowerCase().trim();
  if (!type) return null;

  // 1. Direct alias
  if (TYPE_ALIASES[type]) return TYPE_ALIASES[type];

  // 2. Partial match — check if input contains any alias key
  for (const [alias, taxKey] of Object.entries(TYPE_ALIASES)) {
    if (type.includes(alias) || alias.includes(type)) return taxKey;
  }

  // 3. Tag-based matching — search taxonomy tags
  let bestMatch = null;
  let bestScore = 0;
  const words = type.split(/[\s\-_\/]+/).filter(w => w.length > 2);

  for (const [taxKey, taxData] of Object.entries(TAXONOMY)) {
    if (taxKey === '_Generic') continue;
    const tags = taxData.tags || [];
    const allText = [taxKey.toLowerCase(), ...tags].join(' ');
    let score = 0;
    for (const word of words) {
      if (allText.includes(word)) score += 1;
    }
    // Bonus for exact taxonomy key match
    if (taxKey.toLowerCase().includes(type) || type.includes(taxKey.toLowerCase())) score += 5;
    if (score > bestScore) {
      bestScore = score;
      bestMatch = taxKey;
    }
  }

  if (bestScore >= 1) return bestMatch;

  // 4. Fallback to generic
  return '_Generic';
}

/**
 * getSmartSuggestions — AI-driven suggestion engine
 *
 * @param {string} equipmentType — equipment type/description
 * @param {string} sectionId — target section (failuremodels, scheduledtasks, etc.)
 * @param {string} equipmentId — selected equipment ID
 * @param {Array} existingData — current data in section (for dedup)
 * @param {Object} projectContext — optional: { failureHistory, weibullResults, rcmFindings }
 * @returns {Array} ranked suggestions
 */
function getSmartSuggestions(equipmentType, sectionId, equipmentId, existingData = [], projectContext = {}) {
  const resolvedType = resolveEquipmentType(equipmentType);
  if (!resolvedType) return [];

  const taxonomy = TAXONOMY[resolvedType];
  if (!taxonomy) return [];

  // Map section IDs to taxonomy data keys
  const sectionMap = {
    failuremodels: 'failuremodes', failuremodes: 'failuremodes',
    consequences: 'consequences',
    scheduledtasks: 'scheduledtasks', correctivetasks: 'scheduledtasks',
    rcmcauses: 'failuremodes', rcmFunctions: 'functions', functions: 'functions'
  };

  const dataKey = sectionMap[sectionId];
  if (!dataKey || !taxonomy[dataKey]) return [];

  const existingDescs = new Set(existingData.map(d => (d.description || '').toLowerCase()));
  const timestamp = Date.now();

  let suggestions = taxonomy[dataKey]
    .filter(item => !existingDescs.has((item.description || '').toLowerCase()))
    .map((item, idx) => ({
      ...item,
      id: `SUG-${timestamp}-${String(idx + 1).padStart(3, '0')}`,
      equipmentId: equipmentId,
      equipment: equipmentType,
      source: `${taxonomy.standard || 'ISO 14224'} / ${resolvedType}`,
      _isSuggestion: true,
      _suggestionRank: idx + 1,
      _resolvedType: resolvedType,
      _category: taxonomy.category
    }));

  // ========================================================================
  // CROSS-MODULE INTELLIGENCE — boost/adjust based on project context
  // ========================================================================

  // Boost severity for failure modes that match existing failure history
  if (projectContext.failureHistory && dataKey === 'failuremodes') {
    const historyDescs = (projectContext.failureHistory || []).map(h => (h.description || '').toLowerCase());
    suggestions = suggestions.map(s => {
      const desc = (s.description || '').toLowerCase();
      const hasHistory = historyDescs.some(h => {
        const words = desc.split(/\s+/).filter(w => w.length > 3);
        return words.some(w => h.includes(w));
      });
      if (hasHistory) {
        return { ...s, severity: Math.min(10, (s.severity || 5) + 1), _boosted: true, _boostReason: 'Matches failure history' };
      }
      return s;
    });
  }

  // Boost PM tasks that align with Weibull findings (high beta = wear-out)
  if (projectContext.weibullResults && dataKey === 'scheduledtasks') {
    const hasWearOut = (projectContext.weibullResults || []).some(w => w.beta > 1.5);
    if (hasWearOut) {
      suggestions = suggestions.map(s => {
        if (s.strategy === 'Fixed Time') {
          return { ...s, _boosted: true, _boostReason: 'Weibull β>1.5 indicates wear-out — fixed-time replacement effective' };
        }
        return s;
      });
    }
  }

  // Sort: boosted first, then by severity (descending), then by rank
  suggestions.sort((a, b) => {
    if (a._boosted && !b._boosted) return -1;
    if (!a._boosted && b._boosted) return 1;
    if ((b.severity || 0) !== (a.severity || 0)) return (b.severity || 0) - (a.severity || 0);
    return (a._suggestionRank || 99) - (b._suggestionRank || 99);
  });

  return suggestions;
}

export { TAXONOMY, TYPE_ALIASES, resolveEquipmentType, getSmartSuggestions };
